import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Pause, Play } from 'lucide-react';
import { trackEvent, trackPageView, trackStoryGenerated, trackAppDownload } from '../services/analytics';
import { getFromGlobalStore } from '../utils/globalAudioStore';
import { buildStoryData, splitTextIntoChunks, MAX_CHUNK_CHARS } from '../utils/storyTextProcessor';
import { pushStoryStateToServer } from '../utils/storyState';

const FALLBACK_API_URL = 'https://taraanaya-prod-production.up.railway.app';
const API_URL = process.env.REACT_APP_BACKEND_URL || FALLBACK_API_URL;

// Clean any leaked technical markers from narration text
const cleanNarrationText = (text) => {
  if (!text) return '';
  return text
    .replace(/---\s*[A-Z_]+\s*---/gi, '')    // Remove ---MARKER--- patterns
    .replace(/===\s*[A-Z_]+\s*===/gi, '')    // Remove ===MARKER=== patterns
    .replace(/\b[A-Z_]{4,}---/g, '')          // Remove FUTURE_MD--- partial markers
    .replace(/\bFUTURE_MD\b/g, '')
    .replace(/\bPAST_MD\b/g, '')
    .replace(/\bFUTURE_NARRATIVES\b/g, '')
    .replace(/\bPAST_NARRATIVES\b/g, '')
    .replace(/###SECT_[A-Z_]+###/g, '')
    .replace(/###\s*/g, '')
    .replace(/---+/g, '')                      // Remove --- separators
    .replace(/\*\*([^*]+)\*\*/g, '$1')         // Remove **bold** markers but keep text
    .replace(/#{1,3}\s+/g, '')                 // Remove ## headers
    .replace(/^\s*OVERVIEW\s*$/gim, '')         // Remove OVERVIEW headers
    .replace(/^\s*Domain\s*\d+\s*$/gim, '')     // Remove "Domain 1" headers
    // Remove duplicate mahadasha headers: "Venus Mahadasha (2007-2027)\n" at start of text
    .replace(/^\s*\w+\s+Mahadasha\s*\(\d{4}\s*[-–to]+\s*\d{4}\)\s*\n?/i, '')
    // Also remove mahadasha headers that appear after newlines (e.g., mid-text duplicates)
    .replace(/\n\s*\w+\s+Mahadasha\s*\(\d{4}\s*[-–to]+\s*\d{4}\)\s*\n/gi, '\n')
    .replace(/\n{3,}/g, '\n\n')                // Collapse multiple newlines
    .trim();
};

// Planet icons and colors
const PLANET_ICONS = {
  Sun: '☉', Moon: '☽', Mars: '♂', Mercury: '☿', Jupiter: '♃',
  Venus: '♀', Saturn: '♄', Rahu: '☊', Ketu: '☋'
};

const PLANET_COLORS = {
  Sun: '#FFD700', Moon: '#C8C8FF', Mars: '#DC143C', Mercury: '#32CD32',
  Jupiter: '#FFA500', Venus: '#FF69B4', Saturn: '#8B4513', Rahu: '#4B0082', Ketu: '#006400'
};

// Language configuration - Hindi (left) | Hinglish (center) | English (right)
const LANGUAGES = ['hindi', 'hinglish', 'english'];
const LANGUAGE_LABELS = { hindi: 'हिंदी', hinglish: 'Hinglish', english: 'English' };

// Domain filter chips
const DOMAIN_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'health', label: 'Health' },
  { key: 'career', label: 'Career' },
  { key: 'wealth', label: 'Wealth' },
  { key: 'marriage', label: 'Marriage' },
  { key: 'education', label: 'Education' },
  { key: 'family', label: 'Family' },
  { key: 'travel', label: 'Travel' },
  { key: 'spirituality', label: 'Spiritual' }
];

// TTS Configuration - Using Edge TTS (FREE MadhurNeural)
const TTS_ENDPOINT = '/api/tts/edge';

const PastStoryRevolutionary = () => {
  const navigate = useNavigate();
  
  // Core states
  // Initialize loading state based on whether cached data exists
  // This prevents showing "Loading your story..." flash when data is already available
  const [isLoading, setIsLoading] = useState(() => {
    const cachedAiStory = localStorage.getItem('ai_story_data');
    const birthDetails = localStorage.getItem('birthDetails') || localStorage.getItem('birth_details');
    return !(cachedAiStory && birthDetails);
  });
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    try {
      return localStorage.getItem('ps_selected_language') || 'hinglish';
    } catch { return 'hinglish'; }
  });
  const [personName, setPersonName] = useState('');
  const [pastMahadashas, setPastMahadashas] = useState([]);
  
  // Audio states - per language tracking
  const [audioChunks, setAudioChunks] = useState({ hindi: [], hinglish: [], english: [] });
  const [playbackPosition, setPlaybackPosition] = useState({ hindi: 0, hinglish: 0, english: 0 });
  // Track exact time position within chunk for accurate resume
  const [audioTimePosition, setAudioTimePosition] = useState({ hindi: 0, hinglish: 0, english: 0 });
  
  // Playback states
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentChunkIndex, setCurrentChunkIndex] = useState(0);
  const [currentMahadashaIndex, setCurrentMahadashaIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [showSkipButton, setShowSkipButton] = useState(false);
  const [storyComplete, setStoryComplete] = useState(false);
  
  // Filter states
  const [selectedDomain, setSelectedDomain] = useState(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const domainParam = urlParams.get('domain');
      if (domainParam) return domainParam;
      return localStorage.getItem('ps_selected_domain') || 'all';
    } catch { return 'all'; }
  });
  // Reel mode: hide filters, show only one domain (like DomainReelsStory)
  const isReelMode = new URLSearchParams(window.location.search).get('mode') === 'reel';
  const [selectedMahadashaFilter, setSelectedMahadashaFilter] = useState(() => {
    try {
      // Fresh entry from planet click (reel mode) → always start from the beginning.
      // This prevents the story from resuming at a previously-selected Mahadasha.
      if (isReelMode) {
        localStorage.setItem('ps_selected_mahadasha', 'null');
        return null;
      }
      const saved = localStorage.getItem('ps_selected_mahadasha');
      return saved !== null && saved !== 'null' ? parseInt(saved) : null;
    } catch { return null; }
  });
  const [showFilterHints, setShowFilterHints] = useState(true);
  
  // Refs
  const audioRef = useRef(null);
  const abortRef = useRef(false);
  const textIntervalRef = useRef(null);
  const storyDataRef = useRef(null);
  const fullStoryTextRef = useRef({ hindi: '', hinglish: '', english: '' });
  const audioUrlCacheRef = useRef({ hindi: {}, hinglish: {}, english: {} });
  const currentLanguageRef = useRef('hinglish'); // Track current language for cleanup
  const previousTextRef = useRef(''); // Track previous text to prevent flickering
  const isUpdatingTextRef = useRef(false); // Prevent multiple updates at same time
  // Refs to track positions reliably (avoid stale closure issues)
  const playbackPositionRef = useRef({ hindi: 0, hinglish: 0, english: 0 });
  const audioTimePositionRef = useRef({ hindi: 0, hinglish: 0, english: 0 });
  // Guard ref to prevent duplicate loadData execution
  const hasLoadedRef = useRef(false);
  // In-flight TTS request deduplication - prevents duplicate API calls
  const inFlightRequestsRef = useRef({});
  // Generation counter - increments on domain/language/filter change to invalidate old playback loops
  const playGenerationRef = useRef(0);

  // Scroll text box to top when text changes
  useEffect(() => {
    const el = document.getElementById('past-text-box');
    if (el) el.scrollTop = 0;
  }, [displayedText]);


  // Smooth text update function to prevent flickering - NO delay, direct update
  const updateDisplayedText = useCallback((newText) => {
    // Skip if same text or currently updating
    if (newText === previousTextRef.current || isUpdatingTextRef.current) return;
    
    isUpdatingTextRef.current = true;
    previousTextRef.current = newText;
    
    // Direct update - no setTimeout to avoid flicker
    setDisplayedText(newText);
    
    // Reset flag after a short delay
    setTimeout(() => {
      isUpdatingTextRef.current = false;
    }, 100);
  }, []);

  // Mark that user has USED their FREE past story (one per device)
  // This is set when the story starts playing, not when page loads
  // So users can see the loading but once story plays, free view is used
  useEffect(() => {
    // Mark that user is watching story (for app resume)
    localStorage.setItem('watching_story', 'true');
    console.log('Started watching Past Story');
    trackPageView('past_story');
    trackEvent('past_story_view', { source: new URLSearchParams(window.location.search).get('source') || 'app' });
    const birthDetails = JSON.parse(localStorage.getItem('birthDetails') || '{}');
    trackStoryGenerated(localStorage.getItem('ps_selected_language') || 'hinglish', birthDetails.name || 'User');
  }, []);

  // Persist playback state to localStorage (survives app kill/battery die)
  useEffect(() => {
    localStorage.setItem('ps_selected_language', selectedLanguage);
    localStorage.setItem('ps_selected_domain', selectedDomain);
    localStorage.setItem('ps_selected_mahadasha', selectedMahadashaFilter !== null ? String(selectedMahadashaFilter) : 'null');
  }, [selectedLanguage, selectedDomain, selectedMahadashaFilter]);

  // Mark story as used when complete (no login prompt)
  // NOTE: past_story_viewed is ONLY set when user presses Skip or Continue
  // This allows replay until user explicitly moves forward
  useEffect(() => {
    if (storyComplete) {
      // Just clear watching_story flag - don't set past_story_viewed yet
      // past_story_viewed will be set when user presses Skip or Continue
      console.log('🎬 Story finished - user can Replay or Continue');
      trackEvent('past_story_complete', { language: selectedLanguage, domain: selectedDomain });
    }
  }, [storyComplete, selectedLanguage, selectedDomain]);

  // CRITICAL: Stop all audio completely - saves position if requested
  const stopAllAudio = useCallback((savePosition = false) => {
    // Save current position before stopping (if requested)
    if (savePosition && audioRef.current) {
      const currentTime = audioRef.current.currentTime || 0;
      setAudioTimePosition(prev => ({ ...prev, [currentLanguageRef.current]: currentTime }));
    }
    
    // Stop current audio
    if (audioRef.current) {
      audioRef.current.pause();
      // Don't reset currentTime - preserve position for resume
      audioRef.current.src = '';
      audioRef.current.load(); // Force release
      audioRef.current = null;
    }
    
    // Stop global audio reference
    if (window.currentPastAudio) {
      window.currentPastAudio.pause();
      window.currentPastAudio.src = '';
      window.currentPastAudio = null;
    }
    
    // Clear any pending text intervals
    if (textIntervalRef.current) {
      clearInterval(textIntervalRef.current);
      textIntervalRef.current = null;
    }
    
    // Set abort flag
    abortRef.current = true;
  }, []);

  // Generate intro text for a language
  const generateIntroText = useCallback((language, userName, mahadashaCount) => {
    if (language === 'hindi') {
      return `नमस्ते ${userName}!\n\nमैं आपको आपके बीते हुए जीवन की कहानी सुनाने जा रहा हूं। आपके ${mahadashaCount} महादशाओं का सफर।`;
    } else if (language === 'english') {
      return `Hello ${userName}!\n\nLet me tell you the story of your past life. Your journey through ${mahadashaCount} Mahadashas.`;
    } else {
      return `Namaste ${userName}!\n\nMain aapko aapke beete huye jeevan ki kahani sunane ja raha hoon. Aapke ${mahadashaCount} Mahadashas ka safar.`;
    }
  }, []);

  // Generate full story text for a language AND track chunk-to-mahadasha mapping
  // All 8 domains in EXACT backend order: health, career, wealth, marriage, education, family, travel, spirituality
  const ALL_DOMAINS = [
    { key: 'health', name: 'Health & Longevity', icon: '❤️' },
    { key: 'career', name: 'Career & Success', icon: '💼' },
    { key: 'wealth', name: 'Wealth & Finance', icon: '💰' },
    { key: 'marriage', name: 'Love & Marriage', icon: '💍' },
    { key: 'education', name: 'Education & Skills', icon: '📚' },
    { key: 'family', name: 'Family & Children', icon: '👨‍👩‍👧' },
    { key: 'travel', name: 'Travel & Foreign', icon: '✈️' },
    { key: 'spirituality', name: 'Spirituality & Dharma', icon: '🕉️' }
  ];

  // Helper: detect which mahadasha lord a paragraph primarily discusses
  const detectMahadashaFromText = (text, pastMds) => {
    if (!text || !pastMds || pastMds.length === 0) return { index: -1, lord: null };
    const textLower = text.toLowerCase();
    
    let bestMatch = { index: -1, lord: null, score: 0 };
    
    pastMds.forEach((md, idx) => {
      const lord = (md.lord || '').toLowerCase();
      if (!lord) return;
      
      let score = 0;
      // Count mentions of this lord
      const lordRegex = new RegExp(lord, 'gi');
      const mentions = (text.match(lordRegex) || []).length;
      score += mentions * 2;
      
      // Check for explicit patterns (higher weight)
      const explicitPatterns = [
        new RegExp(`${lord}\\s*(ki|ka|ke|'s)?\\s*(dasha|mahadasha|period|phase)`, 'i'),
        new RegExp(`(during|under|in)\\s+${lord}`, 'i'),
        new RegExp(`${lord}\\s+(ke dauran|ke samay|mein|influence)`, 'i'),
        new RegExp(`^${lord}\\s*:`, 'i'),
        new RegExp(`${lord}\\s+mahadasha\\s*\\(`, 'i')
      ];
      for (const p of explicitPatterns) {
        if (p.test(text)) score += 5;
      }
      
      // Check if year range mentioned matches this mahadasha
      const years = md.years || '';
      if (years && textLower.includes(years.toLowerCase())) score += 3;
      
      if (score > bestMatch.score) {
        bestMatch = { index: idx, lord: md.lord, score };
      }
    });
    
    // Only assign if score is meaningful (at least a mention + context)
    return bestMatch.score >= 4 ? bestMatch : { index: -1, lord: null };
  };

  const generateFullStoryText = useCallback((data, language) => {
    if (!data) return { fullText: '', sections: [] };
    
    // Get mahadashas including current one
    const pastMds = data.past_story?.mahadashas || 
                   data.all_mahadashas?.filter(m => m.status === 'past' || m.status === 'current') || [];
    const userName = data.user_info?.name || 'User';
    
    let fullText = '';
    const sections = []; // Track which section each part belongs to
    
    // Language-specific text templates
    const langText = {
      hindi: {
        intro: `${userName}, आज मैं आपको आपकी पूरी पिछली कहानी सुनाता हूं। आपकी जन्म कुंडली के एल्गोरिदम से निकाली गई यह कहानी है - हर क्षेत्र, हर महादशा, सब कुछ।`,
        timelineIntro: `अब देखते हैं टाइमलाइन - हर महादशा में क्या हुआ।`,
        currentSuffix: ', अभी चल रहा है',
        domainIn: ' में: ',
        endStatus: (year) => `यह थी आपकी पूरी पिछली कहानी ${year} तक। आपकी यात्रा जारी है।`
      },
      english: {
        intro: `${userName}, today I will tell you your complete past story. This story is derived from your birth chart algorithm - every domain, every mahadasha, everything.`,
        timelineIntro: `Now let's look at the timeline - what happened in each mahadasha.`,
        currentSuffix: ', currently ongoing',
        domainIn: ' in: ',
        endStatus: (year) => `This was your complete past story until ${year}. Your journey continues.`
      },
      hinglish: {
        intro: `${userName}, aaj main aapko aapki poori past story sunata hoon. Aapki birth chart ke algorithm se nikali gayi ye kahani hai - har domain, har mahadasha, sab kuch.`,
        timelineIntro: `Ab dekhte hain timeline - har mahadasha mein kya hua.`,
        currentSuffix: ', abhi chal raha hai',
        domainIn: ' mein: ',
        endStatus: (year) => `Ye thi aapki poori past story ${year} tak. Aapki journey continue hai.`
      }
    };
    
    const lang = langText[language] || langText.hinglish;
    
    // ===== Check for pre-parsed sections from backend =====
    const storySections = data.past_story?.sections || {};
    const introStories = data.past_story?.intro || {};
    const algoDriven = data.past_story?.algorithm_driven || {};
    
    // PATH 1: Pre-parsed sections from backend (best quality)
    if (storySections[language] && Array.isArray(storySections[language]) && storySections[language].length > 0) {
      console.log(`✅ Using pre-parsed sections for ${language}: ${storySections[language].length} sections`);
      
      storySections[language].forEach((section) => {
        fullText += section.text + '\n\n';
        sections.push({
          text: section.text,
          mahadashaIndex: section.mahadasha_index ?? -1,
          type: section.type,
          domain: section.domain,
          mahadashaLord: section.mahadasha_lord,
          isPart1: section.type === 'intro' || section.type === 'domain'
        });
      });
      
    // PATH 2: Build sections from algorithm_driven structured data
    } else if (algoDriven && (algoDriven.intro?.[language] || algoDriven.domain_sections?.[language] || algoDriven.stories?.[language])) {
      console.log(`📊 Building sections from algorithm_driven data for ${language}`);
      
      // 2a. Add intro
      const algoIntro = algoDriven.intro?.[language];
      if (algoIntro) {
        fullText += algoIntro + '\n\n';
        sections.push({ text: algoIntro, mahadashaIndex: -1, type: 'intro', domain: null, mahadashaLord: null, isPart1: true });
      }
      
      // 2b+2c. DOMAIN-CENTRIC: For each domain → overview → all MDs for that domain
      const domainSections = algoDriven.domain_sections?.[language] || {};
      const domainOrder = ['health', 'career', 'wealth', 'marriage', 'education', 'family', 'travel', 'spirituality'];
      const mdSections = algoDriven.mahadasha_sections?.[language] || [];
      const pastMdSections = algoDriven.past_mahadasha_sections?.[language];
      const targetMdSections = pastMdSections || mdSections;
      
      // Build domain overview text map for duplicate detection
      const domainOverviewMap = {};
      for (const [dom, ds] of Object.entries(domainSections)) {
        const ov = typeof ds === 'object' ? (ds?.narrative || '') : (typeof ds === 'string' ? ds : '');
        if (ov) domainOverviewMap[dom] = ov.substring(0, 80);
      }
      
      // Build per-lord domain_predictions map from all_mahadashas (engine results)
      const allMdPredictions = {};
      const allMds = data.all_mahadashas || [];
      for (const amd of allMds) {
        const lord = amd.lord || amd.name;
        if (lord && amd.domain_predictions) {
          allMdPredictions[lord] = amd.domain_predictions;
        }
      }
      
      // Format engine prediction into readable text
      const formatPrediction = (pred, domain, lordName) => {
        if (!pred || typeof pred !== 'object') return '';
        const parts = [];
        const hinglishSummary = pred.hinglish_summary;
        const detailedSummary = pred.detailed_summary || pred.summary || '';
        if (hinglishSummary) {
          parts.push(hinglishSummary);
        } else if (detailedSummary) {
          parts.push(detailedSummary);
        }
        const focus = pred.focus_areas;
        if (Array.isArray(focus) && focus.length > 0) {
          parts.push(`Focus areas: ${focus.join(', ')}.`);
        }
        const advice = pred.advice;
        if (advice && typeof advice === 'string') {
          parts.push(advice);
        }
        const risks = pred.disease_risks;
        if (Array.isArray(risks) && risks.length > 0) {
          parts.push(`Risks: ${risks.join(', ')}.`);
        }
        return parts.join(' ');
      };
      
      // Helper: extract lord-specific sentences from a combined narrative
      const extractLordSentences = (text, lordName) => {
        if (!text || !lordName) return text;
        const allLords = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu',
          'Surya', 'Chandra', 'Mangal', 'Budh', 'Guru', 'Shukra', 'Shani'];
        const otherLords = allLords.filter(l => l.toLowerCase() !== lordName.toLowerCase());
        const mentionsOtherLords = otherLords.some(l => text.toLowerCase().includes(l.toLowerCase() + ' '));
        if (!mentionsOtherLords) return null; // Not a combined narrative, return null to trigger fallback
        
        // Split into sentences and find ones mentioning THIS lord
        const sentences = text.split(/(?<=[.!?।])\s+/).filter(s => s.trim().length > 10);
        const lordLower = lordName.toLowerCase();
        const lordAliases = { 'Sun': 'surya', 'Moon': 'chandra', 'Mars': 'mangal', 'Mercury': 'budh',
          'Jupiter': 'guru', 'Venus': 'shukra', 'Saturn': 'shani', 'Rahu': 'rahu', 'Ketu': 'ketu' };
        const alias = lordAliases[lordName] || '';
        const lordSpecific = sentences.filter(s => {
          const sl = s.toLowerCase();
          return sl.includes(lordLower) || (alias && sl.includes(alias));
        });
        return lordSpecific.length > 0 ? lordSpecific.join(' ') : null;
      };
      
      // Helper: resolve MD text for a specific domain
      const resolveMdDomainText = (md, idx, domain) => {
        const mdLord = (typeof md === 'object' ? md?.lord : null) || pastMds[idx]?.lord || 'Unknown';
        const domainImpacts = typeof md === 'object' ? md?.domain_impacts : null;
        if (!domainImpacts || typeof domainImpacts !== 'object' || Object.keys(domainImpacts).length === 0) return null;
        
        let impactText = domainImpacts[domain];
        let finalText = '';
        
        if (impactText && typeof impactText === 'string' && impactText.length > 20) {
          const isDuplicateOfOverview = domainOverviewMap[domain] && 
            impactText.substring(0, 80) === domainOverviewMap[domain];
          if (isDuplicateOfOverview) {
            const enginePred = allMdPredictions[mdLord]?.[domain] || 
              allMdPredictions[mdLord]?.[domain === 'spirituality' ? 'spiritual' : domain];
            finalText = formatPrediction(enginePred, domain, mdLord);
          } else {
            const extracted = extractLordSentences(impactText, mdLord);
            if (extracted) {
              finalText = extracted;
            } else {
              const enginePred = allMdPredictions[mdLord]?.[domain] || 
                allMdPredictions[mdLord]?.[domain === 'spirituality' ? 'spiritual' : domain];
              const engineText = formatPrediction(enginePred, domain, mdLord);
              finalText = engineText || impactText;
            }
          }
        }
        
        if (!finalText || finalText.length < 20) {
          const domKey = domain === 'spirituality' ? 'spiritual' : domain;
          const enginePred = allMdPredictions[mdLord]?.[domKey] || allMdPredictions[mdLord]?.[domain];
          finalText = formatPrediction(enginePred, domain, mdLord);
        }
        
        return (finalText && finalText.length > 15) ? { text: finalText, mdLord } : null;
      };
      
      // DOMAIN-CENTRIC LOOP: For each domain → overview → all MDs → next domain
      const oldFormatMDs = [];
      
      for (const domain of domainOrder) {
        // Domain overview first
        const ds = domainSections[domain];
        const overviewText = typeof ds === 'object' ? (ds?.narrative || '') : (typeof ds === 'string' ? ds : '');
        // Skip "analysis pending" fallback text - it means GPT failed
        if (overviewText && overviewText.length > 30 && !overviewText.toLowerCase().includes('analysis pending')) {
          fullText += overviewText + '\n\n';
          sections.push({ text: overviewText, mahadashaIndex: -1, type: 'domain', domain, mahadashaLord: null, isPart1: true });
        }
        
        // Then all mahadasha content for THIS domain
        if (Array.isArray(targetMdSections)) {
          targetMdSections.forEach((md, idx) => {
            const mdLord = (typeof md === 'object' ? md?.lord : null) || pastMds[idx]?.lord || 'Unknown';
            const domainImpacts = typeof md === 'object' ? md?.domain_impacts : null;
            
            if (domainImpacts && typeof domainImpacts === 'object' && Object.keys(domainImpacts).length > 0) {
              const resolved = resolveMdDomainText(md, idx, domain);
              if (resolved) {
                fullText += resolved.text + '\n\n';
                sections.push({
                  text: resolved.text, mahadashaIndex: idx, type: 'mahadasha_domain', domain,
                  mahadashaLord: resolved.mdLord, isPart1: false
                });
              }
            } else if (domain === domainOrder[0]) {
              // Track old-format MDs only once (first domain iteration)
              oldFormatMDs.push({ md, idx, mdLord });
            }
          });
        }
      }
      
      // Append old-format mahadashas (no domain_impacts) at the end
      for (const { md, idx, mdLord } of oldFormatMDs) {
        const narrative = typeof md === 'object' ? (md?.narrative || '') : (typeof md === 'string' ? md : '');
        if (narrative && narrative.length > 30) {
          fullText += narrative + '\n\n';
          sections.push({
            text: narrative, mahadashaIndex: idx, type: 'mahadasha', domain: null,
            mahadashaLord: mdLord, isPart1: false
          });
        }
      }
      
      // 2d. If still no domain/mahadasha sections, use the full stories text
      if (sections.length <= 1) {
        const fullStory = algoDriven.stories?.[language] || '';
        if (fullStory && fullStory.length > 200) {
          const paragraphs = cleanNarrationText(fullStory).split(/\n\n+/).filter(p => p.trim().length > 50);
          paragraphs.forEach((para, idx) => {
            fullText += para + '\n\n';
            const detected = detectMahadashaFromText(para, pastMds);
            const isIntroOrDomain = idx < Math.min(10, paragraphs.length / 3);
            sections.push({
              text: para.trim(), 
              mahadashaIndex: detected.index !== -1 ? detected.index : (isIntroOrDomain ? -1 : Math.min(idx - 10, pastMds.length - 1)),
              mahadashaLord: detected.lord,
              type: detected.index !== -1 ? 'mahadasha' : (idx === 0 ? 'intro' : (isIntroOrDomain ? 'domain' : 'mahadasha')), 
              isPart1: isIntroOrDomain && detected.index === -1
            });
          });
        }
      }
      
    // PATH 3: Full story text in intro field (long string > 500 chars)
    } else if (introStories[language] && typeof introStories[language] === 'string' && introStories[language].length > 500) {
      const godLevelStory = cleanNarrationText(introStories[language]);
      console.log(`Using intro[${language}]: ${godLevelStory.length} chars`);
      
      const paragraphs = godLevelStory.split(/\n\n+/).filter(p => p.trim().length > 50);
      
      paragraphs.forEach((para, idx) => {
        fullText += para + '\n\n';
        const detected = detectMahadashaFromText(para, pastMds);
        const isIntroOrDomain = idx < Math.min(10, paragraphs.length / 3) && detected.index === -1;
        sections.push({
          text: para.trim(), 
          mahadashaIndex: detected.index !== -1 ? detected.index : (isIntroOrDomain ? -1 : Math.min(idx - 10, pastMds.length - 1)),
          mahadashaLord: detected.lord,
          type: detected.index !== -1 ? 'mahadasha' : (idx === 0 ? 'intro' : (isIntroOrDomain ? 'domain' : 'mahadasha')), 
          isPart1: isIntroOrDomain
        });
      });
      
    } else {
      // PATH 4: Old structure fallback
      console.log(`⚠️ Fallback: Using old structure for ${language}`);
      const intro = data.past_story?.intro?.[language] || generateIntroText(language, userName, pastMds.length);
      if (intro) {
        fullText += intro + '\n\n';
        sections.push({ text: intro, mahadashaIndex: -1, type: 'intro', isPart1: true });
      }
      
      const timelineNarrations = data.past_story?.timeline_narrations?.[language] || [];
      pastMds.forEach((md, idx) => {
        if (timelineNarrations[idx]) {
          fullText += timelineNarrations[idx] + '\n\n';
          sections.push({ 
            text: timelineNarrations[idx], mahadashaIndex: idx, type: 'narration', isPart1: false,
            mahadashaLord: md.lord, mahadashaYears: md.years, mahadashaColor: md.color
          });
        }
      });
      
      // PATH 4b: If still very short, also check domain_narratives
      const domainNarratives = data.past_story?.domain_narratives;
      if (domainNarratives && sections.length <= 1) {
        const domainOrder = ['health', 'career', 'wealth', 'marriage', 'education', 'family', 'travel', 'spirituality'];
        for (const domain of domainOrder) {
          const dn = domainNarratives[domain]?.[language] || domainNarratives[domain];
          const text = typeof dn === 'string' ? dn : (typeof dn === 'object' ? (dn?.narrative || dn?.text || '') : '');
          if (text && text.length > 30) {
            fullText += text + '\n\n';
            sections.push({ text, mahadashaIndex: -1, type: 'domain', domain, isPart1: true });
          }
        }
      }
    }
    
    // ===== SUPPLEMENTAL PASS: Create mahadasha_domain chunks if missing =====
    // This runs AFTER any PATH to ensure per-domain per-mahadasha filtering works
    const hasMahadashadomainSections = sections.some(s => s.type === 'mahadasha_domain');
    if (!hasMahadashadomainSections && pastMds.length > 0) {
      console.log('🔧 No mahadasha_domain sections found. Supplementing from engine predictions + algorithm_driven.');
      
      // Build domain overview map for duplicate detection
      const domainOverviewMap = {};
      sections.filter(s => s.type === 'domain').forEach(s => {
        if (s.domain && s.text) domainOverviewMap[s.domain] = s.text.substring(0, 80);
      });
      
      // Build engine predictions map
      const allMdPredictions = {};
      const allMds = data.all_mahadashas || [];
      for (const amd of allMds) {
        const lord = amd.lord || amd.name;
        if (lord && amd.domain_predictions) allMdPredictions[lord] = amd.domain_predictions;
      }
      
      // Get algorithm_driven mahadasha_sections for domain_impacts
      const algoMdSections = algoDriven.mahadasha_sections?.[language] || 
                             algoDriven.past_mahadasha_sections?.[language] || [];
      
      // Format engine prediction into readable text
      const fmtPred = (pred) => {
        if (!pred || typeof pred !== 'object') return '';
        const parts = [];
        if (pred.hinglish_summary) parts.push(pred.hinglish_summary);
        else if (pred.detailed_summary || pred.summary) parts.push(pred.detailed_summary || pred.summary);
        if (Array.isArray(pred.focus_areas) && pred.focus_areas.length) parts.push(`Focus areas: ${pred.focus_areas.join(', ')}.`);
        if (pred.advice) parts.push(pred.advice);
        if (Array.isArray(pred.disease_risks) && pred.disease_risks.length) parts.push(`Risks: ${pred.disease_risks.join(', ')}.`);
        return parts.join(' ');
      };
      
      // Extract lord-specific sentences from combined text
      const extractForLord = (text, lordName) => {
        if (!text || !lordName) return null;
        const otherLords = ['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu',
          'Surya','Chandra','Mangal','Budh','Guru','Shukra','Shani'].filter(l => l.toLowerCase() !== lordName.toLowerCase());
        if (!otherLords.some(l => text.toLowerCase().includes(l.toLowerCase() + ' '))) return null;
        const sentences = text.split(/(?<=[.!?।])\s+/).filter(s => s.trim().length > 10);
        const lordLower = lordName.toLowerCase();
        const aliases = {'Sun':'surya','Moon':'chandra','Mars':'mangal','Mercury':'budh',
          'Jupiter':'guru','Venus':'shukra','Saturn':'shani','Rahu':'rahu','Ketu':'ketu'};
        const alias = aliases[lordName] || '';
        const specific = sentences.filter(s => { const sl = s.toLowerCase(); return sl.includes(lordLower) || (alias && sl.includes(alias)); });
        return specific.length > 0 ? specific.join(' ') : null;
      };
      
      const impactDomains = ['health', 'career', 'wealth', 'marriage', 'education', 'family', 'travel', 'spirituality'];
      
      pastMds.forEach((md, idx) => {
        const mdLord = md.lord || 'Unknown';
        const algoMd = algoMdSections[idx];
        const domainImpacts = algoMd?.domain_impacts || {};
        
        for (const domain of impactDomains) {
          let finalText = '';
          const diText = domainImpacts[domain];
          
          if (diText && typeof diText === 'string' && diText.length > 20) {
            // Check if duplicate of overview
            const isDup = domainOverviewMap[domain] && diText.substring(0, 80) === domainOverviewMap[domain];
            if (!isDup) {
              // Try lord-specific extraction
              const extracted = extractForLord(diText, mdLord);
              if (extracted) finalText = extracted;
            }
          }
          
          // Fallback to engine predictions
          if (!finalText || finalText.length < 20) {
            const domKey = domain === 'spirituality' ? 'spiritual' : domain;
            const pred = allMdPredictions[mdLord]?.[domKey] || allMdPredictions[mdLord]?.[domain];
            finalText = fmtPred(pred);
          }
          
          if (finalText && finalText.length > 15) {
            fullText += finalText + '\n\n';
            sections.push({
              text: finalText, mahadashaIndex: idx, type: 'mahadasha_domain', domain,
              mahadashaLord: mdLord, isPart1: false
            });
          }
        }
      });
    }
    
    // ===== REORDER SECTIONS: Domain-centric sequence =====
    // New order: Intro → [Health overview + Health MD1 + Health MD2...] → [Career overview + Career MDs...] → ...
    const domainOrder2 = ['health', 'career', 'wealth', 'marriage', 'education', 'family', 'travel', 'spirituality'];
    const introSections = sections.filter(s => s.type === 'intro');
    const reordered = [...introSections];
    
    for (const dom of domainOrder2) {
      // Domain overview first
      const overviews = sections.filter(s => s.type === 'domain' && (s.domain === dom || s.domain === dom.replace('spirituality', 'spiritual')));
      reordered.push(...overviews);
      // Then all mahadasha discussions for this domain, ordered by mahadasha index
      const mds = sections
        .filter(s => s.type === 'mahadasha_domain' && (s.domain === dom || s.domain === dom.replace('spirituality', 'spiritual')))
        .sort((a, b) => (a.mahadashaIndex || 0) - (b.mahadashaIndex || 0));
      reordered.push(...mds);
    }
    
    // Append any generic (non-domain) mahadasha sections at the end
    const remaining = sections.filter(s => !reordered.includes(s) && s.type !== 'intro');
    reordered.push(...remaining);
    
    // Rebuild fullText from reordered sections
    const reorderedText = reordered.map(s => s.text).join('\n\n');
    
    return { fullText: reorderedText.trim(), sections: reordered };
  }, [generateIntroText]);

  // Detect iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isIOSWebView = isIOS && (window.webkit?.messageHandlers || window.Capacitor);
  
  // iOS Audio State - track if audio is unlocked
  const audioUnlockedRef = useRef(false);
  const audioElementRef = useRef(null);
  
  // Convert base64 to blob URL - with iOS-specific handling
  const base64ToAudioUrl = (base64) => {
    try {
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'audio/mpeg' }); // Use audio/mpeg for better iOS compatibility
      return URL.createObjectURL(blob);
    } catch (e) {
      console.error('❌ base64ToAudioUrl error:', e);
      return null;
    }
  };
  
  // iOS-specific: Convert base64 to data URL (more reliable in iOS WebView)
  const base64ToDataUrl = (base64) => {
    return `data:audio/mpeg;base64,${base64}`;
  };

  // Get audio for a chunk - from cache (instant!) or generate if needed
  // Returns { blobUrl, dataUrl } for iOS compatibility
  // Uses in-flight deduplication: if pre-fetch already started for this chunk, reuse that Promise
  const getAudioForChunk = async (text, language, chunkIndex) => {
    // Use text-based cache key to avoid cache collision when filtered chunks have different indices
    const cacheKey = `${language}_${text.substring(0, 60).replace(/\s+/g, '_')}`;
    
    // LAYER 1: In-memory URL cache (instant — 0ms)
    if (audioUrlCacheRef.current[cacheKey]) {
      return audioUrlCacheRef.current[cacheKey];
    }
    
    // LAYER 2: Global audio store (saved by LoadingStory — 0ms)
    const globalAudio = getFromGlobalStore(language, text);
    if (globalAudio) {
      audioUrlCacheRef.current[cacheKey] = globalAudio;
      return globalAudio;
    }
    
    // Check if there's already an in-flight request for this chunk
    if (inFlightRequestsRef.current[cacheKey]) {
      return inFlightRequestsRef.current[cacheKey];
    }
    
    // Create a new request and store the Promise for deduplication
    const requestPromise = (async () => {
      try {
        const response = await fetch(`${API_URL}${TTS_ENDPOINT}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: text.substring(0, 600), language })
        });
        
        if (response.ok) {
          const result = await response.json();
          
          if (result.success && result.audio) {
            let audioData;
            if (isIOSWebView) {
              audioData = { 
                dataUrl: base64ToDataUrl(result.audio),
                blobUrl: null
              };
            } else {
              audioData = { 
                blobUrl: base64ToAudioUrl(result.audio),
                dataUrl: base64ToDataUrl(result.audio)
              };
            }
            
            // Cache using text-based key
            audioUrlCacheRef.current[cacheKey] = audioData;
            return audioData;
          }
        }
      } catch (e) {
        console.error(`TTS fetch error: ${e.message}`);
      } finally {
        // Remove from in-flight after completion
        delete inFlightRequestsRef.current[cacheKey];
      }
      return null;
    })();
    
    // Store the Promise so subsequent calls for same chunk reuse it
    inFlightRequestsRef.current[cacheKey] = requestPromise;
    return requestPromise;
  };

  // Batch pre-fetch ALL audio for a language
  // LAYER 1: Check globalAudioStore (instant — saved by LoadingStory during same session)
  // LAYER 2: Batch API endpoint (server-side cache hits are < 0.5s for ALL chunks)
  const batchPreFetchAudio = async (chunks, language) => {
    if (!chunks || chunks.length === 0) return;
    
    // FIRST: Populate audioUrlCacheRef from globalAudioStore (instant, 0ms per chunk)
    const uncachedIndices = [];
    for (let i = 0; i < chunks.length; i++) {
      const cacheKey = `${language}_${chunks[i].text.substring(0, 60).replace(/\s+/g, '_')}`;
      if (audioUrlCacheRef.current[cacheKey]) continue;
      
      // Check globalAudioStore (populated by LoadingStory same-session)
      const globalAudio = getFromGlobalStore(language, chunks[i].text);
      if (globalAudio) {
        audioUrlCacheRef.current[cacheKey] = globalAudio;
        continue;
      }
      uncachedIndices.push(i);
    }
    
    if (uncachedIndices.length === 0) {
      console.log(`All ${chunks.length} chunks cached for ${language}`);
      return;
    }
    
    console.log(`Pre-fetching ${uncachedIndices.length}/${chunks.length} chunks for ${language} via batch API...`);
    
    // Fire ALL batches in PARALLEL (not sequential) for maximum speed
    const BATCH_SIZE = 10;
    const batchPromises = [];
    for (let b = 0; b < uncachedIndices.length; b += BATCH_SIZE) {
      const batchIndices = uncachedIndices.slice(b, b + BATCH_SIZE);
      batchPromises.push(
        fetch(`${API_URL}/api/tts/edge/batch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chunks: batchIndices.map(idx => chunks[idx].text.substring(0, 600)),
            language
          })
        }).then(async (response) => {
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.audio_chunks) {
              for (let j = 0; j < data.audio_chunks.length; j++) {
                const audio = data.audio_chunks[j];
                if (!audio) continue;
                const idx = batchIndices[j];
                const cacheKey = `${language}_${chunks[idx].text.substring(0, 60).replace(/\s+/g, '_')}`;
                const audioData = isIOSWebView
                  ? { dataUrl: base64ToDataUrl(audio), blobUrl: null }
                  : { blobUrl: base64ToAudioUrl(audio), dataUrl: base64ToDataUrl(audio) };
                audioUrlCacheRef.current[cacheKey] = audioData;
              }
            }
          }
        }).catch(() => {})
      );
    }
    await Promise.all(batchPromises);
    
    console.log(`Pre-fetch complete for ${language}`);
  };

  // Start text reveal animation - FRESH TEXT (no scrolling)
  const startTextReveal = (fullText, durationMs) => {
    if (textIntervalRef.current) {
      clearInterval(textIntervalRef.current);
    }
    
    // Simply show the full chunk text immediately - no word-by-word reveal
    // This ensures fresh text appears without scrolling
    updateDisplayedText(fullText);
  };

  // iOS Audio Context unlock - SIMPLIFIED VERSION
  // Called on first user tap
  const unlockAudioContext = useCallback(() => {
    console.log('🔓 Unlocking Audio...');
    
    // Create and unlock Web AudioContext
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext) {
        const ctx = new AudioContext();
        const buffer = ctx.createBuffer(1, 1, 22050);
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        source.start(0);
        
        if (ctx.state === 'suspended') {
          ctx.resume().catch(() => {});
        }
        console.log('✅ Web AudioContext unlocked');
      }
    } catch (e) {
      console.log('AudioContext unlock failed:', e.message);
    }
    
    // Prime the HTML audio element with silent audio
    const audio = document.getElementById('past-story-audio');
    if (audio) {
      const silentAudio = 'data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYGq6FAAAAAAAAAAAAAAAAAAAAA';
      audio.src = silentAudio;
      audio.volume = 0.01;
      
      audio.play()
        .then(() => {
          console.log('✅ Audio element primed');
          audio.pause();
          audio.volume = 1.0;
        })
        .catch((e) => {
          console.log('⚠️ Audio prime skipped:', e.message);
        });
    }
    
    audioUnlockedRef.current = true;
    console.log('🔓 Audio unlock complete');
  }, []);

  // Play a single audio chunk - SIMPLIFIED VERSION
  const playAudioChunk = (audioData, chunkText, startTime = 0) => {
    return new Promise((resolve) => {
      // Always show text first
      updateDisplayedText(chunkText);
      
      if (abortRef.current || !audioData) {
        console.log('playAudioChunk: No audio data or aborted');
        // Wait for reading time
        const readTime = Math.max(chunkText.split(/\s+/).length * 250, 2000);
        setTimeout(resolve, readTime);
        return;
      }
      
      console.log(`🎵 playAudioChunk: Starting playback`);
      
      // Get the audio URL (prefer dataUrl for iOS)
      const audioUrl = audioData.dataUrl || audioData.blobUrl || audioData;
      
      if (!audioUrl) {
        console.error('❌ No valid audio URL');
        setTimeout(resolve, 3000);
        return;
      }
      
      // Use the DOM audio element
      let audio = document.getElementById('past-story-audio');
      if (!audio) {
        audio = new Audio();
        audio.id = 'past-story-audio-fallback';
      }
      
      // Store reference
      audioRef.current = audio;
      
      let resolved = false;
      const safeResolve = () => {
        if (!resolved) {
          resolved = true;
          resolve();
        }
      };
      
      // Event handlers
      audio.onended = () => {
        console.log('🎵 Audio ended');
        safeResolve();
      };
      
      audio.onerror = (e) => {
        console.error('🔴 Audio error:', e.target?.error?.message || 'unknown');
        safeResolve();
      };
      
      // Set source and play
      audio.src = audioUrl;
      audio.volume = 1.0;
      
      if (startTime > 0) {
        audio.currentTime = startTime;
      }
      
      audio.play()
        .then(() => {
          console.log('✅ Audio playing');
        })
        .catch((e) => {
          console.error('🔴 Play failed:', e.message);
          // Wait for reading time then continue
          const readTime = Math.max(chunkText.split(/\s+/).length * 250, 3000);
          setTimeout(safeResolve, readTime);
        });
      
      // Fallback timeout (60 seconds max)
      setTimeout(() => {
        if (!resolved) {
          console.log('⏰ Audio timeout');
          safeResolve();
        }
      }, 60000);
    });
  };

  // Play the story from a specific position
  const playStory = async (lang, startFromIndex = 0, startTime = 0) => {
    console.log(`playStory called: lang=${lang}, startFromIndex=${startFromIndex}, startTime=${startTime}`);
    
    // Use filtered chunks based on domain/mahadasha selection
    const chunks = getFilteredChunks(lang);
    console.log(`  filtered chunks: ${chunks?.length || 0} (domain: ${selectedDomain}, mdFilter: ${selectedMahadashaFilter})`);
    
    if (!chunks || chunks.length === 0) {
      console.error('No audio chunks available for filters:', selectedDomain, selectedMahadashaFilter);
      return;
    }
    
    // CRITICAL: Stop any existing audio first
    stopAllAudio();
    
    // Small delay to ensure cleanup
    await new Promise(r => setTimeout(r, 50));
    
    // Capture current generation - if this changes, we MUST stop immediately
    const myGeneration = playGenerationRef.current;
    
    setIsPlaying(true);
    setShowSkipButton(true);
    setStoryComplete(false);
    abortRef.current = false;
    currentLanguageRef.current = lang;
    
    // NOTE: past_story_viewed / free_story_used are NOT set here on auto-play.
    // They are ONLY set when user explicitly presses Skip, Login, or Continue
    // (so closing the app on the planets/story screen resumes the same flow).
    
    console.log(`▶️ Playing ${lang} from chunk ${startFromIndex}/${chunks.length}, gen=${myGeneration}`);
    
    for (let i = startFromIndex; i < chunks.length; i++) {
      // Check BOTH abort flag AND generation counter
      if (abortRef.current || myGeneration !== playGenerationRef.current) {
        console.log(`⏸️ Playback stopped at chunk ${i} (abort=${abortRef.current}, genMismatch=${myGeneration !== playGenerationRef.current})`);
        break;
      }
      
      const currentChunk = chunks[i];
      setCurrentChunkIndex(i);
      
      // Update playback position for this language BEFORE playing - both ref and state
      playbackPositionRef.current[lang] = i;
      setPlaybackPosition(prev => ({ ...prev, [lang]: i }));
      
      // Use the mahadasha index directly from the chunk (map -1 intro to 0)
      if (selectedMahadashaFilter !== null) {
        setCurrentMahadashaIndex(selectedMahadashaFilter);
      } else {
        setCurrentMahadashaIndex(Math.max(0, currentChunk.mahadashaIndex));
      }
      
      // Show text ONLY if generation still matches (prevents cross-domain text leak)
      if (myGeneration === playGenerationRef.current) {
        updateDisplayedText(currentChunk.text);
      } else {
        break; // Domain changed while we were processing - stop NOW
      }
      
      // PRE-FETCH: While we play chunk i, pre-fetch chunk i+1 and i+2 in background
      if (i + 1 < chunks.length) {
        getAudioForChunk(chunks[i + 1].text, lang, i + 1);
      }
      if (i + 2 < chunks.length) {
        getAudioForChunk(chunks[i + 2].text, lang, i + 2);
      }
      
      // Get audio data (from cache if pre-fetched, or generate)
      const audioData = await getAudioForChunk(currentChunk.text, lang, i);
      
      // Check AGAIN after async fetch
      if (abortRef.current || myGeneration !== playGenerationRef.current) {
        console.log(`⏸️ Playback aborted after fetch at chunk ${i}`);
        break;
      }
      
      if (audioData) {
        const chunkStartTime = (i === startFromIndex) ? startTime : 0;
        await playAudioChunk(audioData, currentChunk.text, chunkStartTime);
      } else {
        console.log(`⚠️ No audio for chunk ${i}, quick text display`);
        const readingTime = Math.max(currentChunk.text.split(/\s+/).length * 150, 1500);
        await new Promise(r => setTimeout(r, readingTime));
      }
      
      // Check AGAIN after playback
      if (abortRef.current || myGeneration !== playGenerationRef.current) break;
      
      // Quick transition between chunks
      if (i < chunks.length - 1) {
        await new Promise(r => setTimeout(r, 50));
      }
    }
    
    // Only complete if we didn't get aborted AND generation still matches
    if (!abortRef.current && myGeneration === playGenerationRef.current) {
      setIsPlaying(false);
      setShowSkipButton(false);
      setStoryComplete(true);
      setPlaybackPosition(prev => ({ ...prev, [lang]: chunks.length }));
      setAudioTimePosition(prev => ({ ...prev, [lang]: 0 }));
      console.log(`✅ ${lang} story playback complete!`);
    }
  };

  // Toggle play/pause - SINGLE BUTTON FOR EVERYTHING
  const handlePlayPauseToggle = () => {
    console.log('🔘 handlePlayPauseToggle called');
    console.log('  isPlaying:', isPlaying);
    console.log('  selectedLanguage:', selectedLanguage);
    console.log('  audioChunks:', Object.keys(audioChunks).map(k => `${k}:${audioChunks[k]?.length || 0}`).join(', '));
    
    if (isPlaying) {
      // PAUSE - Save exact position BEFORE stopping audio
      const currentTime = audioRef.current?.currentTime || 0;
      const currentPos = playbackPosition[selectedLanguage] || 0;
      
      // Save to BOTH state and ref for reliable access
      audioTimePositionRef.current[selectedLanguage] = currentTime;
      playbackPositionRef.current[selectedLanguage] = currentPos;
      setAudioTimePosition(prev => ({ ...prev, [selectedLanguage]: currentTime }));
      
      // CRITICAL: Stop all audio properly (don't save again)
      stopAllAudio(false);
      
      setIsPlaying(false);
      
      console.log(`⏸️ Paused ${selectedLanguage} at chunk ${currentPos}, time: ${currentTime.toFixed(2)}s`);
    } else {
      // CRITICAL: Unlock iOS audio on first user tap
      unlockAudioContext();
      
      // PLAY or RESUME - Use REF values for reliable access
      const currentPos = playbackPositionRef.current[selectedLanguage] || playbackPosition[selectedLanguage] || 0;
      const savedTime = audioTimePositionRef.current[selectedLanguage] || audioTimePosition[selectedLanguage] || 0;
      const chunks = getFilteredChunks(selectedLanguage);
      const totalChunks = chunks?.length || 0;
      
      console.log(`▶️ Play requested: ${selectedLanguage}, pos=${currentPos}/${totalChunks}, savedTime=${savedTime.toFixed(2)}s, complete=${storyComplete}`);
      
      if (storyComplete || currentPos >= totalChunks) {
        // Story complete - replay from beginning
        console.log(`🔄 Replaying ${selectedLanguage} from beginning`);
        playbackPositionRef.current[selectedLanguage] = 0;
        audioTimePositionRef.current[selectedLanguage] = 0;
        setPlaybackPosition(prev => ({ ...prev, [selectedLanguage]: 0 }));
        setAudioTimePosition(prev => ({ ...prev, [selectedLanguage]: 0 }));
        setCurrentChunkIndex(0);
        setCurrentMahadashaIndex(0);
        setStoryComplete(false);
        playStory(selectedLanguage, 0, 0);
      } else {
        // Resume from saved position AND exact time
        console.log(`▶️ Resuming ${selectedLanguage} from chunk ${currentPos}, time ${savedTime.toFixed(2)}s`);
        playStory(selectedLanguage, currentPos, savedTime);
      }
    }
  };

  // Handle language change - INSTANT SWITCH
  const handleLanguageChange = (newLang) => {
    if (newLang === selectedLanguage) return;
    
    console.log(`🔄 INSTANT SWITCH: ${selectedLanguage} → ${newLang}`);
    
    // STEP 1: Save current position FIRST before any changes - to BOTH ref and state
    const currentTime = audioRef.current?.currentTime || 0;
    const currentPos = playbackPosition[selectedLanguage] || 0;
    
    audioTimePositionRef.current[selectedLanguage] = currentTime;
    playbackPositionRef.current[selectedLanguage] = currentPos;
    setAudioTimePosition(prev => ({ ...prev, [selectedLanguage]: currentTime }));
    
    console.log(`💾 Saved ${selectedLanguage} position: chunk ${currentPos}, time ${currentTime.toFixed(2)}s`);
    
    // STEP 2: Immediately abort and stop everything
    playGenerationRef.current++; // Invalidate any running playback
    abortRef.current = true;
    
    // STEP 3: Stop audio immediately (don't reset currentTime)
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    
    // STEP 4: Switch language state immediately
    setSelectedLanguage(newLang);
    currentLanguageRef.current = newLang;
    setIsPlaying(false);
    
    // STEP 5: Get new language chunks and display at saved position
    const newChunks = audioChunks[newLang];
    const newPos = playbackPositionRef.current[newLang] || playbackPosition[newLang] || 0;
    const newTime = audioTimePositionRef.current[newLang] || audioTimePosition[newLang] || 0;
    
    console.log(`📍 Restoring ${newLang} position: chunk ${newPos}, time ${newTime.toFixed(2)}s`);
    
    if (newChunks && newChunks.length > 0) {
      const displayPos = Math.min(newPos, newChunks.length - 1);
      const chunk = newChunks[displayPos];
      
      console.log(`Displaying ${newLang} chunk ${displayPos}/${newChunks.length}`);
      
      if (chunk) {
        // Immediately update UI
        updateDisplayedText(chunk.text);
        setCurrentChunkIndex(displayPos);
        setCurrentMahadashaIndex(Math.max(0, chunk.mahadashaIndex));
      }
      
      // PRE-FETCH ALL chunks for new language (non-blocking)
      batchPreFetchAudio(newChunks, newLang);
      
      // Check completion status
      const isComplete = newPos >= newChunks.length;
      setStoryComplete(isComplete);
    } else {
      console.log(`⚠️ No chunks for ${newLang}`);
      updateDisplayedText('');
      setCurrentChunkIndex(0);
      setCurrentMahadashaIndex(-1);
    }
    
    // STEP 6: Reset abort flag for new playback
    setTimeout(() => {
      abortRef.current = false;
    }, 100);
  };

  // Helper: check if chunk text is primarily about a specific mahadasha lord
  const chunkMatchesMahadashaLord = useCallback((chunk, lordName) => {
    if (!lordName || !chunk.text) return false;
    const text = chunk.text.toLowerCase();
    const lord = lordName.toLowerCase();
    
    // Check if chunk's mahadashaLord field matches
    if (chunk.mahadashaLord && chunk.mahadashaLord.toLowerCase() === lord) return true;
    
    // Text-based matching: check if the text primarily discusses this lord
    // Look for patterns like "Mars ki dasha", "Mars Mahadasha", "Under Mars influence"
    const lordMentions = (text.match(new RegExp(lord, 'gi')) || []).length;
    
    // Check other lord names to see if THIS lord is the primary one mentioned
    const otherLords = ['sun', 'moon', 'mars', 'mercury', 'jupiter', 'venus', 'saturn', 'rahu', 'ketu']
      .filter(l => l !== lord);
    const otherMentions = otherLords.reduce((sum, l) => {
      return sum + (text.match(new RegExp(l, 'gi')) || []).length;
    }, 0);
    
    // Lord is primary if mentioned more than other lords combined, or mentioned at least twice with some context
    if (lordMentions >= 2 && lordMentions > otherMentions) return true;
    
    // Check for explicit patterns: "Lord ki dasha", "Lord Mahadasha", "Lord period" 
    const explicitPatterns = [
      new RegExp(`${lord}\\s*(ki|ka|ke|'s)?\\s*(dasha|mahadasha|period|phase)`, 'i'),
      new RegExp(`(during|under|in)\\s+${lord}`, 'i'),
      new RegExp(`${lord}\\s+(mahadasha|influence|ke dauran|ke samay|mein)`, 'i')
    ];
    if (explicitPatterns.some(p => p.test(chunk.text))) return true;
    
    // If text starts with "Lord:" pattern
    if (text.startsWith(lord + ':') || text.startsWith(lord + ' :')) return true;
    
    return false;
  }, []);

  // Get filtered chunks based on selected domain and mahadasha filters
  // Filter matrix (DOMAIN-CENTRIC):
  //   All + No MD     → everything (Health Overview → Health MDs → Career Overview → Career MDs → ...)
  //   Domain + No MD  → domain overview + ALL MDs for that domain
  //   Domain + MD     → ONLY that domain's MD content (skip overview)
  //   All + MD        → that MD's content across all domains (skip overviews)
  const getFilteredChunks = useCallback((lang) => {
    const allChunks = audioChunks[lang] || [];
    if (selectedDomain === 'all' && selectedMahadashaFilter === null) return allChunks;
    
    const domainMatch = (chunk, d) => {
      return chunk.domain === d || 
        chunk.domain === d.replace('spirituality', 'spiritual') ||
        chunk.domain === d.replace('spiritual', 'spirituality');
    };
    
    // Get the selected mahadasha lord name for text-based fallback
    const selectedLord = selectedMahadashaFilter !== null && pastMahadashas[selectedMahadashaFilter] 
      ? pastMahadashas[selectedMahadashaFilter].lord 
      : null;
    
    let filtered;
    
    if (selectedDomain !== 'all' && selectedMahadashaFilter !== null) {
      // DOMAIN + SPECIFIC MD: ONLY that domain's content for that mahadasha (skip overview)
      filtered = allChunks.filter(chunk => {
        if (chunk.type === 'mahadasha_domain') {
          return chunk.mahadashaIndex === selectedMahadashaFilter && domainMatch(chunk, selectedDomain);
        }
        return false;
      });
      // FALLBACK: old-style mahadasha chunks
      if (filtered.length === 0) {
        filtered = allChunks.filter(chunk => {
          if (chunk.type === 'mahadasha' && chunk.mahadashaIndex === selectedMahadashaFilter) return true;
          return false;
        });
      }
    } else if (selectedDomain !== 'all') {
      // DOMAIN ONLY (no mahadasha): show domain overview + ALL mahadasha events for that domain
      filtered = allChunks.filter(chunk => {
        if (chunk.type === 'domain' && domainMatch(chunk, selectedDomain)) return true;
        // Include domain-specific mahadasha sections
        if (chunk.type === 'mahadasha_domain') return domainMatch(chunk, selectedDomain);
        return false;
      });
      // Fallback: if no mahadasha_domain chunks, show old-style mahadasha chunks
      if (!filtered.some(c => c.type === 'mahadasha_domain')) {
        filtered = allChunks.filter(chunk => {
          if (chunk.type === 'domain' && domainMatch(chunk, selectedDomain)) return true;
          if (chunk.type === 'mahadasha') return true; // old-style, show all
          return false;
        });
      }
    } else if (selectedMahadashaFilter !== null) {
      // ALL DOMAINS + SPECIFIC MD: only that MD's content across all domains (skip overviews)
      filtered = allChunks.filter(chunk => {
        if (chunk.type === 'mahadasha_domain') {
          return chunk.mahadashaIndex === selectedMahadashaFilter;
        }
        if (chunk.type === 'mahadasha' && chunk.mahadashaIndex === selectedMahadashaFilter) return true;
        return false;
      });
    } else {
      filtered = allChunks;
    }
    
    // Text-based fallback: if index-based filtering returned 0 mahadasha results
    if (selectedLord && selectedMahadashaFilter !== null && filtered.length === 0) {
      console.log(`⚠️ Filter returned 0 chunks. Trying text-based matching for ${selectedLord}`);
      const textMatched = allChunks.filter(chunk => {
        if (selectedDomain !== 'all') {
          return chunkMatchesMahadashaLord(chunk, selectedLord) && domainMatch(chunk, selectedDomain);
        }
        return chunkMatchesMahadashaLord(chunk, selectedLord);
      });
      if (textMatched.length > 0) return textMatched;
    }
    
    return filtered;
  }, [audioChunks, selectedDomain, selectedMahadashaFilter, pastMahadashas, chunkMatchesMahadashaLord]);

  // Handle domain filter change
  const handleDomainChange = (domainKey) => {
    if (domainKey === selectedDomain) return;
    setShowFilterHints(false);
    
    // CRITICAL: Increment generation counter FIRST - instantly invalidates any running playStory loop
    playGenerationRef.current++;
    abortRef.current = true;
    
    // Stop current audio
    stopAllAudio();
    setIsPlaying(false);
    setStoryComplete(false);
    
    // Reset positions for current language
    playbackPositionRef.current[selectedLanguage] = 0;
    audioTimePositionRef.current[selectedLanguage] = 0;
    setPlaybackPosition(prev => ({ ...prev, [selectedLanguage]: 0 }));
    setAudioTimePosition(prev => ({ ...prev, [selectedLanguage]: 0 }));
    setCurrentChunkIndex(0);
    
    setSelectedDomain(domainKey);
    
    // IMMEDIATELY show first chunk of new domain (no setTimeout - prevents race)
    const chunks = audioChunks[selectedLanguage] || [];
    let filtered;
    const dm = (chunk, d) => chunk.domain === d || chunk.domain === d.replace('spirituality', 'spiritual') || chunk.domain === d.replace('spiritual', 'spirituality');
    if (domainKey === 'all' && selectedMahadashaFilter === null) {
      filtered = chunks;
    } else if (domainKey !== 'all' && selectedMahadashaFilter !== null) {
      // Domain + Specific MD: ONLY that domain's mahadasha content (skip overview)
      filtered = chunks.filter(chunk => {
        if (chunk.type === 'mahadasha_domain') return chunk.mahadashaIndex === selectedMahadashaFilter && dm(chunk, domainKey);
        return false;
      });
      if (filtered.length === 0) {
        filtered = chunks.filter(chunk => {
          if (chunk.type === 'mahadasha' && chunk.mahadashaIndex === selectedMahadashaFilter) return true;
          return false;
        });
      }
    } else if (domainKey !== 'all') {
      // Domain only: show domain overview + all mahadasha events for that domain
      filtered = chunks.filter(chunk => {
        if (chunk.type === 'domain' && dm(chunk, domainKey)) return true;
        if (chunk.type === 'mahadasha_domain') return dm(chunk, domainKey);
        return false;
      });
      if (!filtered.some(c => c.type === 'mahadasha_domain')) {
        filtered = chunks.filter(chunk => {
          if (chunk.type === 'domain' && dm(chunk, domainKey)) return true;
          if (chunk.type === 'mahadasha') return true;
          return false;
        });
      }
    } else if (selectedMahadashaFilter !== null) {
      // All + Specific MD: only that MD's content (skip overviews)
      filtered = chunks.filter(chunk => {
        if (chunk.type === 'mahadasha_domain') return chunk.mahadashaIndex === selectedMahadashaFilter;
        if (chunk.type === 'mahadasha' && chunk.mahadashaIndex === selectedMahadashaFilter) return true;
        return false;
      });
    } else {
      filtered = chunks;
    }
    if (filtered && filtered.length > 0) {
      updateDisplayedText(filtered[0].text);
      setCurrentMahadashaIndex(selectedMahadashaFilter !== null ? selectedMahadashaFilter : Math.max(0, filtered[0].mahadashaIndex));
      // PRE-FETCH first 3 chunks of new domain so play starts instantly
      const lang = selectedLanguage;
      for (let p = 0; p < Math.min(3, filtered.length); p++) {
        getAudioForChunk(filtered[p].text, lang, p);
      }
    } else {
      updateDisplayedText('');
    }
  };

  // Handle mahadasha timeline click
  const handleMahadashaFilterClick = (idx) => {
    // Toggle: if already selected, deselect
    const newFilter = selectedMahadashaFilter === idx ? null : idx;
    setShowFilterHints(false);
    
    // CRITICAL: Increment generation counter to invalidate running playback
    playGenerationRef.current++;
    abortRef.current = true;
    
    // Stop current audio
    stopAllAudio();
    setIsPlaying(false);
    setStoryComplete(false);
    
    // Reset positions
    playbackPositionRef.current[selectedLanguage] = 0;
    audioTimePositionRef.current[selectedLanguage] = 0;
    setPlaybackPosition(prev => ({ ...prev, [selectedLanguage]: 0 }));
    setAudioTimePosition(prev => ({ ...prev, [selectedLanguage]: 0 }));
    setCurrentChunkIndex(0);
    
    setSelectedMahadashaFilter(newFilter);
    if (newFilter !== null) setCurrentMahadashaIndex(newFilter);
    
    // Show first chunk of filtered result
    setTimeout(() => {
      const chunks = audioChunks[selectedLanguage] || [];
      if (newFilter === null && selectedDomain === 'all') {
        // No filters - show first chunk
        if (chunks.length > 0) {
          updateDisplayedText(chunks[0].text);
          setCurrentMahadashaIndex(Math.max(0, chunks[0].mahadashaIndex));
        }
        return;
      }
      
      const dm = (chunk, d) => chunk.domain === d || chunk.domain === d.replace('spirituality', 'spiritual') || chunk.domain === d.replace('spiritual', 'spirituality');
      
      let filtered;
      if (selectedDomain !== 'all' && newFilter !== null) {
        // Domain + Specific MD: ONLY that domain's mahadasha content (skip overview)
        filtered = chunks.filter(chunk => {
          if (chunk.type === 'mahadasha_domain') return chunk.mahadashaIndex === newFilter && dm(chunk, selectedDomain);
          return false;
        });
        if (filtered.length === 0) {
          filtered = chunks.filter(chunk => {
            if (chunk.type === 'mahadasha' && chunk.mahadashaIndex === newFilter) return true;
            return false;
          });
        }
      } else if (selectedDomain !== 'all') {
        // Domain only: domain overview + all mahadasha events for that domain
        filtered = chunks.filter(chunk => {
          if (chunk.type === 'domain' && dm(chunk, selectedDomain)) return true;
          if (chunk.type === 'mahadasha_domain') return dm(chunk, selectedDomain);
          return false;
        });
        if (!filtered.some(c => c.type === 'mahadasha_domain')) {
          filtered = chunks.filter(chunk => {
            if (chunk.type === 'domain' && dm(chunk, selectedDomain)) return true;
            if (chunk.type === 'mahadasha') return true;
            return false;
          });
        }
      } else if (newFilter !== null) {
        // All + Specific MD: only that MD's content (skip overviews)
        filtered = chunks.filter(chunk => {
          if (chunk.type === 'mahadasha_domain') return chunk.mahadashaIndex === newFilter;
          if (chunk.type === 'mahadasha' && chunk.mahadashaIndex === newFilter) return true;
          return false;
        });
      } else {
        filtered = chunks;
      }
      
      if (filtered.length > 0) {
        updateDisplayedText(filtered[0].text);
        setCurrentMahadashaIndex(newFilter !== null ? newFilter : Math.max(0, filtered[0].mahadashaIndex));
        // PRE-FETCH first 3 chunks of new filter so play starts instantly
        const lang = selectedLanguage;
        for (let p = 0; p < Math.min(3, filtered.length); p++) {
          getAudioForChunk(filtered[p].text, lang, p);
        }
      } else {
        updateDisplayedText('');
      }
    }, 50);
  };



  // Handle skip - jump to end and mark as viewed
  const handleSkip = () => {
    // CRITICAL: Stop all audio completely
    stopAllAudio();
    
    // Mark past story as viewed - user explicitly chose to skip
    localStorage.setItem('free_story_used', 'true');
    localStorage.setItem('past_story_viewed', 'true');
    localStorage.removeItem('watching_story');
    // Save to Capacitor persistent storage (survives WebView clears on Android/iOS)
    try { window.Capacitor?.Plugins?.Preferences?.set({ key: 'past_story_viewed', value: 'true' }); } catch(e){}
    try { window.Capacitor?.Plugins?.Preferences?.set({ key: 'free_story_used', value: 'true' }); } catch(e){}
    // Clear playback position state since user finished
    localStorage.removeItem('ps_selected_language');
    localStorage.removeItem('ps_selected_domain');
    localStorage.removeItem('ps_selected_mahadasha');
    console.log('User skipped Past Story - marked as viewed');
    
    // Mark story as viewed on server (so other devices skip to login)
    const cacheKey = localStorage.getItem('story_cache_key');
    if (cacheKey) {
      const _did = localStorage.getItem('taraanaya_device_id') || localStorage.getItem('device_id') || '';
      fetch(`${API_URL}/api/unified-story/mark-viewed/${cacheKey}?device_id=${encodeURIComponent(_did)}`, { method: 'POST', headers: { 'x-device-id': _did } }).catch(() => {});
    }
    // Also persist to device-keyed story_state collection for cross-install survival
    pushStoryStateToServer({ past_story_viewed: true, free_story_used: true, landing_flow: false });
    
    // Mark current language as complete
    const chunks = audioChunks[selectedLanguage];
    setPlaybackPosition(prev => ({ ...prev, [selectedLanguage]: chunks?.length || 0 }));
    setAudioTimePosition(prev => ({ ...prev, [selectedLanguage]: 0 }));
    
    setIsPlaying(false);
    setShowSkipButton(false);
    setStoryComplete(true);
  };

  // Handle replay - reset current language only
  const handleReplay = () => {
    // CRITICAL: Stop all audio first
    stopAllAudio();
    
    setPlaybackPosition(prev => ({ ...prev, [selectedLanguage]: 0 }));
    setAudioTimePosition(prev => ({ ...prev, [selectedLanguage]: 0 }));
    setCurrentChunkIndex(0);
    setCurrentMahadashaIndex(-1);
    setStoryComplete(false);
    
    // Small delay then start playing
    setTimeout(() => {
      playStory(selectedLanguage, 0, 0);
    }, 100);
  };

  // Handle continue - mark as viewed and go to login (or download CTA for landing page)
  const searchParams = new URLSearchParams(window.location.search);
  // Landing page flow: user came through /watch on a non-native browser.
  // PRIMARY signal: URL pathname (/watch or /watch1) — this is authoritative and
  // survives even if `landing_flow` localStorage flag was cleared by a previous
  // download click. SECONDARY: source query param. TERTIARY: landing_flow flag.
  const isNativeApp = !!(typeof window !== 'undefined' && window.Capacitor && typeof window.Capacitor.isNativePlatform === 'function' && window.Capacitor.isNativePlatform());
  const onWatchPath = typeof window !== 'undefined' &&
    (window.location.pathname === '/watch' || window.location.pathname === '/watch1' ||
     window.location.pathname.startsWith('/watch/') || window.location.pathname.startsWith('/watch1/'));
  const landingFlag = typeof window !== 'undefined' && localStorage.getItem('landing_flow') === 'true';
  const isLandingPage = !isNativeApp && (
    onWatchPath ||
    searchParams.get('source') === 'landing' ||
    searchParams.get('source') === 'watch' ||
    landingFlag
  );
  const isWebView = isLandingPage || searchParams.get('source') === 'emergent';
  const [showDownloadCTA, setShowDownloadCTA] = useState(false);

  const handleContinue = () => {
    // Stop any playing audio
    stopAllAudio();
    
    // Mark past story as viewed - user explicitly chose to continue
    localStorage.setItem('free_story_used', 'true');
    localStorage.setItem('past_story_viewed', 'true');
    localStorage.removeItem('watching_story');
    localStorage.removeItem('ps_selected_language');
    localStorage.removeItem('ps_selected_domain');
    localStorage.removeItem('ps_selected_mahadasha');
    // Save to Capacitor persistent storage (survives WebView clears on Android/iOS)
    try { window.Capacitor?.Plugins?.Preferences?.set({ key: 'past_story_viewed', value: 'true' }); } catch(e){}
    try { window.Capacitor?.Plugins?.Preferences?.set({ key: 'free_story_used', value: 'true' }); } catch(e){}

    // Mark story as viewed on server (so other devices skip to login)
    const cacheKey = localStorage.getItem('story_cache_key');
    if (cacheKey) {
      const _did = localStorage.getItem('taraanaya_device_id') || localStorage.getItem('device_id') || '';
      fetch(`${API_URL}/api/unified-story/mark-viewed/${cacheKey}?device_id=${encodeURIComponent(_did)}`, { method: 'POST', headers: { 'x-device-id': _did } }).catch(() => {});
    }
    // Also persist to device-keyed story_state collection for cross-install survival
    pushStoryStateToServer({ past_story_viewed: true, free_story_used: true, landing_flow: false });

    // Landing page users now also go to phone-OTP login (Web-First launch)
    console.log('User pressed Continue - marked as viewed, going to login');
    // For emergent demo (/watch1), pass source so login can allow multiple accounts
    const source = searchParams.get('source');
    navigate(source === 'emergent' ? '/login?source=emergent' : '/login', { replace: true });
  };

  // VISIBILITY CHANGE HANDLER - Pause when tab/app loses focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isPlaying) {
        console.log('👁️ Tab hidden - saving position and pausing');
        // Save current time position
        const currentTime = audioRef.current?.currentTime || 0;
        setAudioTimePosition(prev => ({ ...prev, [selectedLanguage]: currentTime }));
        
        // Stop all audio
        stopAllAudio();
        setIsPlaying(false);
      }
    };
    
    const handleBlur = () => {
      if (isPlaying) {
        console.log('🔇 Window blur - saving position and pausing');
        // Save current time position
        const currentTime = audioRef.current?.currentTime || 0;
        setAudioTimePosition(prev => ({ ...prev, [selectedLanguage]: currentTime }));
        
        // Stop all audio
        stopAllAudio();
        setIsPlaying(false);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
    };
  }, [isPlaying, selectedLanguage, stopAllAudio]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      console.log('🧹 Component unmounting - cleaning up audio');
      stopAllAudio();
    };
  }, [stopAllAudio]);

  // Auto-hide filter hints after 6 seconds
  useEffect(() => {
    const timer = setTimeout(() => setShowFilterHints(false), 6000);
    return () => clearTimeout(timer);
  }, []);


  // Process story data and update states - used for fast path loading
  const processStoryData = useCallback((unifiedStoryData, userName) => {
    storyDataRef.current = unifiedStoryData;
    
    // Get mahadashas
    const pastMds = unifiedStoryData.past_story?.mahadashas || 
                   unifiedStoryData.past_mahadashas ||
                   unifiedStoryData.all_mahadashas?.filter(m => m.status === 'past' || m.status === 'current') || [];
    
    const effectivePastMds = pastMds.length > 0 ? pastMds : 
      (unifiedStoryData.all_mahadashas || []).filter(m => 
        m.status === 'past' || m.status === 'current' || m.is_current
      );
    
    setPastMahadashas(effectivePastMds);
    
    // Prepare audio chunks for all languages
    const newAudioChunks = { hindi: [], hinglish: [], english: [] };
    
    for (const lang of LANGUAGES) {
      const { fullText, sections } = buildStoryData(unifiedStoryData, lang);
      fullStoryTextRef.current[lang] = fullText;
      
      const langChunks = [];
      sections.forEach((section) => {
        const sectionChunks = splitTextIntoChunks(section.text, MAX_CHUNK_CHARS);
        sectionChunks.forEach((chunkText, idx) => {
          langChunks.push({
            text: chunkText,
            index: langChunks.length,
            mahadashaIndex: section.mahadashaIndex,
            mahadashaLord: section.mahadashaLord || null,
            type: section.type,
            domain: section.domain, // CRITICAL: propagate domain for filtering
            sectionPart: idx + 1,
            sectionTotal: sectionChunks.length
          });
        });
      });
      
      newAudioChunks[lang] = langChunks;
    }
    
    setAudioChunks(newAudioChunks);
    
    // Set initial text
    const initialLang = selectedLanguage || 'hinglish';
    if (newAudioChunks[initialLang] && newAudioChunks[initialLang][0]) {
      updateDisplayedText(newAudioChunks[initialLang][0].text);
      // For intro/domain sections (mahadashaIndex -1), show first mahadasha (0)
      setCurrentMahadashaIndex(Math.max(0, newAudioChunks[initialLang][0].mahadashaIndex));
    }
    
    // Audio pre-fetched by LoadingStory → globalAudioStore (instant same-session)
    // Background batch for second visit (server cache hits, non-blocking)
    localStorage.setItem('has_seen_past_story', 'true');
    setIsLoading(false);
    
    // Fire-and-forget: populate audioUrlCacheRef from globalAudioStore + server cache
    for (const lang of LANGUAGES) {
      if (newAudioChunks[lang] && newAudioChunks[lang].length > 0) {
        batchPreFetchAudio(newAudioChunks[lang], lang);
      }
    }
  }, [selectedLanguage, updateDisplayedText]);

  // Load story data on mount - OPTIMIZED for fast display
  useEffect(() => {
    // GUARD: Prevent duplicate execution (React Strict Mode, dependency changes, etc.)
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    const loadData = async () => {
      // FAST PATH: Check for pre-generated data first (no loading spinner needed)
      const cachedAiStory = localStorage.getItem('ai_story_data');
      const birthDetails = localStorage.getItem('birthDetails') || localStorage.getItem('birth_details');
      
      // If we have AI story data, show content immediately without loading spinner
      if (cachedAiStory && birthDetails) {
        try {
          const birthData = JSON.parse(birthDetails);
          const cached = JSON.parse(cachedAiStory);
          const userName = birthData.name || 'User';
          
          if (cached.ai_generated || cached.user_info?.name?.toLowerCase() === userName.toLowerCase()) {
            console.log('⚡ FAST PATH: Using pre-generated AI story');
            setPersonName(userName);
            processStoryData(cached, userName);
            return;
          }
        } catch (e) {
          console.log('⚠️ Fast path failed, using standard loading');
        }
      }
      
      // STANDARD PATH: Show loading spinner
      setIsLoading(true);
      
      if (!birthDetails) {
        navigate('/onboarding-new');
        return;
      }
      
      let birthData;
      try {
        birthData = JSON.parse(birthDetails);
      } catch (e) {
        navigate('/onboarding-new');
        return;
      }
      
      // Check if regeneration is requested via URL parameter
      const urlParams = new URLSearchParams(window.location.search);
      const forceRegenerate = urlParams.get('regenerate') === 'true';
      
      if (forceRegenerate) {
        console.log('🔄 Force regeneration requested - clearing cache and redirecting...');
        localStorage.removeItem('unified_story_data');
        localStorage.removeItem('ai_story_data');
        localStorage.removeItem('audio_generation_complete');
        localStorage.removeItem('audio_cache');
        navigate('/generating-story');
        return;
      }
      
      const userName = birthData.name || 'User';
      setPersonName(userName);
      
      let unifiedStoryData = null;
      
      // Check for cached AI story (generated during loading screen)
      const cachedAiStoryStr = localStorage.getItem('ai_story_data');
      if (cachedAiStoryStr) {
        try {
          const cached = JSON.parse(cachedAiStoryStr);
          console.log('🔍 AI Story Data:', {
            ai_generated: cached.ai_generated,
            has_all_mahadashas: !!cached.all_mahadashas,
            all_mahadashas_count: cached.all_mahadashas?.length || 0,
            past_mahadashas_count: cached.past_mahadashas?.length || 0,
            past_story_mahadashas_count: cached.past_story?.mahadashas?.length || 0,
            user_name: cached.user_info?.name
          });
          if (cached.user_info?.name?.toLowerCase() === userName.toLowerCase() || cached.ai_generated) {
            console.log('✅ Using pre-generated AI story from loading screen');
            unifiedStoryData = cached;
          }
        } catch (e) {
          console.log('⚠️ Failed to parse cached AI story');
          localStorage.removeItem('ai_story_data');
        }
      }
      
      // FALLBACK: Load from localStorage or MongoDB
      if (!unifiedStoryData) {
        console.log('📦 Falling back to cached unified story...');
        const storedData = localStorage.getItem('unified_story_data');
        
        if (storedData) {
          try {
            unifiedStoryData = JSON.parse(storedData);
            console.log('✅ Loaded story from localStorage');
          } catch (e) {
            console.log('Failed to parse stored data');
          }
        }
        
        // Try MongoDB if not in localStorage
        const userId = localStorage.getItem('audio_user_id') || localStorage.getItem('session_id');
        const deviceId = localStorage.getItem('device_id') || localStorage.getItem('taraanaya_device_id');
        const lookupId = userId || deviceId;
        if (!unifiedStoryData && lookupId) {
          try {
            const response = await fetch(`${API_URL}/api/unified-story/load/${lookupId}${deviceId ? '?device_id=' + deviceId : ''}`);
            if (response.ok) {
              const result = await response.json();
              if (result.success && result.story_data) {
                unifiedStoryData = result.story_data;
                console.log('Loaded story from MongoDB (localStorage fallback)');
              } else if (result.success && result.data) {
                unifiedStoryData = result.data;
                console.log('Loaded story from MongoDB (legacy format)');
              }
            }
          } catch (e) {
            console.log('MongoDB load error:', e.message);
          }
        }
      }
      
      if (!unifiedStoryData) {
        navigate('/generating-story');
        return;
      }
      
      processStoryData(unifiedStoryData, userName);
    };
    
    loadData();
  }, [navigate, processStoryData]); // Guard prevents re-execution

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortRef.current = true;
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      if (textIntervalRef.current) {
        clearInterval(textIntervalRef.current);
      }
    };
  }, []);

  // Loading screen
  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#020712',
        display: 'flex',
        justifyContent: 'center'
      }}>
      <div style={{
        width: '100%',
        maxWidth: '680px',
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at center, rgba(237, 193, 97, 0.08) 0%, #020712 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
          style={{
            width: '48px',
            height: '48px',
            border: '3px solid rgba(237, 193, 97, 0.2)',
            borderTopColor: '#edc161',
            borderRadius: '50%',
            marginBottom: '16px'
          }}
        />
        <p style={{ color: '#9CA3AF', fontSize: 'clamp(13px, 3vw, 16px)' }}>Preparing narration...</p>
      </div>
      </div>
    );
  }

  const currentMahadasha = pastMahadashas[currentMahadashaIndex] || {};

  // Responsive sizing based on viewport
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 400;

  return (
    <div style={{
      height: '100dvh',
      maxHeight: '100dvh',
      background: isWebView 
        ? 'radial-gradient(ellipse at center, rgba(237, 193, 97, 0.06) 0%, #020712 60%)'
        : '#020712',
      display: 'flex',
      justifyContent: 'center',
      overflow: 'hidden'
    }}>
    <div style={{
      width: '100%',
      maxWidth: isWebView ? '100%' : '680px',
      height: '100dvh',
      maxHeight: '100dvh',
      background: isWebView ? 'transparent' : 'radial-gradient(ellipse at center, rgba(237, 193, 97, 0.08) 0%, #020712 100%)',
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      touchAction: 'auto',
      paddingTop: 'calc(6px + env(safe-area-inset-top, 0px))',
      paddingRight: isWebView ? 'clamp(16px, 4vw, 60px)' : 'env(safe-area-inset-right, 0px)',
      // Bottom Mahadasha timeline (Moon/Mars/Rahu/Jupiter labels) was being clipped on
      // iPhones with home indicator. Reserve enough room for the labels + the indicator.
      paddingBottom: 'calc(14px + env(safe-area-inset-bottom, 0px))',
      paddingLeft: isWebView ? 'clamp(16px, 4vw, 60px)' : 'env(safe-area-inset-left, 0px)'
    }}>
      {/* Home button — goes back to planets dashboard (always visible, never gets lost) */}
      <button data-testid="home-btn"
        onClick={() => {
          // Clear "watching_story" flag so next app reopen goes to planets dashboard, not back here
          localStorage.removeItem('watching_story');
          const hasData = !!localStorage.getItem('unified_story_data');
          if (hasData) navigate('/loading-past-reels');
          else navigate(-1);
        }}
        style={{
          position: 'fixed', top: 'calc(12px + env(safe-area-inset-top, 0px))', left: 12, zIndex: 200,
          background: 'rgba(10, 22, 40, 0.8)', border: '1px solid rgba(237,193,97,0.3)',
          borderRadius: '20px', padding: '8px 16px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 6,
          backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
        }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#edc161" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        <span style={{
          color: '#F3E5AB',
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 12,
          fontWeight: 500,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
        }}>Planets</span>
      </button>

      {/* Language selector — centered top pill bar, matching Future Reels for visual consistency
          across all 8 domains. Was top-right fixed which truncated labels (Hin/Eng) and overlapped
          content on small screens. Now uses full labels (हिंदी / Hinglish / English) and is
          horizontally centered with safe-area top padding.
          NOTE: In reel mode, this pill bar is shifted DOWN below the Planets back button
          so the two fixed elements never collide on narrow iPhones (was overlapping
          on iPhone 13/14/15 portrait at <=390px width). */}
      {isReelMode && (
        <div style={{
          position: 'fixed',
          top: 'calc(54px + env(safe-area-inset-top, 0px))',
          left: 0, right: 0,
          display: 'flex', justifyContent: 'center',
          zIndex: 200,
          pointerEvents: 'none',
        }}>
          <div style={{
            display: 'flex', gap: 4,
            background: 'rgba(10, 22, 40, 0.95)', backdropFilter: 'blur(15px)',
            padding: '5px 10px', borderRadius: 18,
            border: '1px solid rgba(237, 193, 97, 0.4)',
            pointerEvents: 'auto',
          }}>
            {[{ key: 'hindi', label: 'हिंदी' }, { key: 'hinglish', label: 'Hinglish' }, { key: 'english', label: 'English' }].map(lang => (
              <button key={lang.key} onClick={() => handleLanguageChange(lang.key)}
                style={{
                  padding: '4px 10px', borderRadius: 12, border: 'none',
                  background: selectedLanguage === lang.key ? 'rgba(237,193,97,0.35)' : 'transparent',
                  color: selectedLanguage === lang.key ? '#edc161' : '#9CA3AF',
                  fontSize: 'clamp(10px, 2.5vw, 11px)', fontWeight: 600, cursor: 'pointer',
                  transition: 'all 0.2s'
                }}>{lang.label}</button>
            ))}
          </div>
        </div>
      )}
      {/* Hidden audio element for iOS compatibility - positioned but invisible */}
      <audio 
        id="past-story-audio"
        preload="auto"
        style={{ 
          position: 'absolute',
          width: '1px',
          height: '1px',
          opacity: 0,
          pointerEvents: 'none'
        }}
      />
      
      {/* Subtle stars background - scattered, no repeating pattern */}
      <div style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        opacity: 0.4
      }}>
        {/* Individual scattered stars using absolute positioning */}
        <div style={{ position: 'absolute', width: '2px', height: '2px', borderRadius: '50%', background: 'white', left: '8%', top: '12%', boxShadow: '0 0 3px white' }} />
        <div style={{ position: 'absolute', width: '1px', height: '1px', borderRadius: '50%', background: 'white', left: '23%', top: '5%', boxShadow: '0 0 2px white' }} />
        <div style={{ position: 'absolute', width: '1.5px', height: '1.5px', borderRadius: '50%', background: 'white', left: '45%', top: '18%', boxShadow: '0 0 3px white' }} />
        <div style={{ position: 'absolute', width: '1px', height: '1px', borderRadius: '50%', background: 'white', left: '67%', top: '8%', boxShadow: '0 0 2px white' }} />
        <div style={{ position: 'absolute', width: '2px', height: '2px', borderRadius: '50%', background: 'white', left: '82%', top: '22%', boxShadow: '0 0 3px white' }} />
        <div style={{ position: 'absolute', width: '1px', height: '1px', borderRadius: '50%', background: 'white', left: '15%', top: '35%', boxShadow: '0 0 2px white' }} />
        <div style={{ position: 'absolute', width: '1.5px', height: '1.5px', borderRadius: '50%', background: 'white', left: '38%', top: '42%', boxShadow: '0 0 3px white' }} />
        <div style={{ position: 'absolute', width: '1px', height: '1px', borderRadius: '50%', background: 'white', left: '58%', top: '38%', boxShadow: '0 0 2px white' }} />
        <div style={{ position: 'absolute', width: '2px', height: '2px', borderRadius: '50%', background: 'white', left: '78%', top: '48%', boxShadow: '0 0 3px white' }} />
        <div style={{ position: 'absolute', width: '1px', height: '1px', borderRadius: '50%', background: 'white', left: '92%', top: '55%', boxShadow: '0 0 2px white' }} />
        <div style={{ position: 'absolute', width: '1.5px', height: '1.5px', borderRadius: '50%', background: 'white', left: '5%', top: '62%', boxShadow: '0 0 3px white' }} />
        <div style={{ position: 'absolute', width: '1px', height: '1px', borderRadius: '50%', background: 'white', left: '28%', top: '72%', boxShadow: '0 0 2px white' }} />
        <div style={{ position: 'absolute', width: '2px', height: '2px', borderRadius: '50%', background: 'white', left: '52%', top: '65%', boxShadow: '0 0 3px white' }} />
        <div style={{ position: 'absolute', width: '1px', height: '1px', borderRadius: '50%', background: 'white', left: '72%', top: '78%', boxShadow: '0 0 2px white' }} />
        <div style={{ position: 'absolute', width: '1.5px', height: '1.5px', borderRadius: '50%', background: 'white', left: '88%', top: '85%', boxShadow: '0 0 3px white' }} />
        <div style={{ position: 'absolute', width: '1px', height: '1px', borderRadius: '50%', background: 'white', left: '18%', top: '88%', boxShadow: '0 0 2px white' }} />
        <div style={{ position: 'absolute', width: '2px', height: '2px', borderRadius: '50%', background: 'white', left: '42%', top: '92%', boxShadow: '0 0 3px white' }} />
        <div style={{ position: 'absolute', width: '1px', height: '1px', borderRadius: '50%', background: 'white', left: '95%', top: '32%', boxShadow: '0 0 2px white' }} />
      </div>

      {/* ===== TOP SECTION: Language + Title (Fixed Height ~12%) ===== */}
      <div style={{ 
        flexShrink: 0,
        // Reserve room for fixed top elements:
        //  - Non-reel: Planets back button (~44px) + safe-area
        //  - Reel: Planets back button (~44px) + Language pills (~36px row below) + safe-area
        // Numbers tuned for iPhone 13/14/15 portrait so title never tucks under the pills.
        paddingTop: isReelMode ? 'clamp(98px, 13vh, 112px)' : 'clamp(54px, 7vh, 66px)',
        paddingBottom: '6px',
        paddingLeft: 'clamp(12px, 3vw, 24px)',
        paddingRight: 'clamp(12px, 3vw, 24px)',
        maxWidth: isWebView ? 'clamp(600px, 70vw, 1100px)' : 'none',
        margin: isWebView ? '0 auto' : 0,
        width: '100%'
      }}>
        {/* Language Pills - Inline, not fixed — HIDE in reel mode (has its own fixed bar) */}
        {!isReelMode && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '6px'
        }}>
          {/* Language Selector */}
          <div style={{
            display: 'flex',
            gap: '6px',
            background: 'rgba(10, 22, 40, 0.95)',
            backdropFilter: 'blur(15px)',
            padding: '6px 14px',
            borderRadius: '20px',
            border: '1px solid rgba(237, 193, 97, 0.4)'
          }}>
            {LANGUAGES.map(lang => (
              <button
                key={lang}
                onClick={() => handleLanguageChange(lang)}
                style={{
                  padding: '5px 10px',
                  borderRadius: '12px',
                  border: 'none',
                  background: selectedLanguage === lang ? 'rgba(237, 193, 97, 0.35)' : 'transparent',
                  color: selectedLanguage === lang ? '#edc161' : '#9CA3AF',
                  fontSize: 'clamp(12px, 3vw, 14px)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {LANGUAGE_LABELS[lang]}
              </button>
            ))}
          </div>
        </div>
        )}

        {/* Title */}
        <div style={{ textAlign: 'center', maxWidth: isWebView ? 'clamp(500px, 65vw, 1000px)' : 'none', margin: isWebView ? '0 auto' : 0 }}>
          <h1 style={{
            fontSize: isWebView ? 'clamp(18px, 2.5vw, 28px)' : 'clamp(20px, 5.5vw, 30px)',
            fontWeight: 700,
            color: '#edc161',
            margin: 0,
            textShadow: '0 0 20px rgba(237, 193, 97, 0.5)'
          }}>
            {personName ? `${personName}'s Past Story` : 'Your Past Story'}
          </h1>
          <p style={{ 
            fontSize: 'clamp(12px, 3vw, 16px)', 
            color: '#9CA3AF', 
            margin: '4px 0 0' 
          }}>
            {selectedDomain !== 'all' || selectedMahadashaFilter !== null ? (
              <span style={{ color: '#edc161' }}>
                {selectedDomain !== 'all' ? DOMAIN_FILTERS.find(d => d.key === selectedDomain)?.label : ''}
                {selectedDomain !== 'all' && selectedMahadashaFilter !== null ? ' • ' : ''}
                {selectedMahadashaFilter !== null && pastMahadashas[selectedMahadashaFilter] ? `${pastMahadashas[selectedMahadashaFilter].lord} Mahadasha` : ''}
              </span>
            ) : (
              <>8 Life Domains • {pastMahadashas.length} Mahadashas</>
            )}
          </p>
        </div>

        {/* Domain Filter Chips — hidden in reel mode (single domain view) */}
        {!isReelMode && (
        <div data-testid="domain-filter-chips" style={{ position: 'relative', maxWidth: isWebView ? 'clamp(500px, 65vw, 1000px)' : 'none', margin: isWebView ? '0 auto' : 0 }}>
          {showFilterHints && (
            <div style={{
              textAlign: 'center', paddingBottom: '4px',
              fontSize: 'clamp(10px, 2.5vw, 13px)', color: 'rgba(237,193,97,0.6)', fontStyle: 'italic',
              animation: 'fadeInUp 0.5s ease'
            }}>
              Choose domain for your past story
            </div>
          )}
          <div style={{
          display: 'flex',
          gap: '6px 5px',
          flexWrap: 'wrap',
          justifyContent: 'center',
          paddingTop: '8px',
          paddingBottom: '4px'
        }}>
          {DOMAIN_FILTERS.map(d => {
            const isActive = selectedDomain === d.key;
            return (
              <button
                key={d.key}
                data-testid={`domain-chip-${d.key}`}
                onClick={() => handleDomainChange(d.key)}
                style={{
                  padding: '4px 10px',
                  borderRadius: '14px',
                  border: `1px solid ${isActive ? '#edc161' : 'rgba(237, 193, 97, 0.25)'}`,
                  background: isActive ? 'rgba(237, 193, 97, 0.25)' : 'rgba(237, 193, 97, 0.06)',
                  color: isActive ? '#edc161' : '#9CA3AF',
                  fontSize: 'clamp(11px, 2.8vw, 13px)',
                  fontWeight: isActive ? 700 : 500,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease',
                  letterSpacing: '0.3px'
                }}
              >
                {d.label}
              </button>
            );
          })}
          </div>
        </div>
        )}
      </div>

      {/* ===== MIDDLE SECTION: Sage + Controls ===== */}
      <div style={{ 
        textAlign: 'center', 
        padding: '2px 12px', 
        position: 'relative',
        flexShrink: 1,
        maxWidth: isWebView ? 'clamp(600px, 70vw, 1100px)' : 'none',
        margin: isWebView ? '0 auto' : 0,
        width: '100%'
      }}>
        {/* Skip Button REMOVED from individual domain reels — only on "View All Domains" screen */}

        {/* Sage Avatar - Responsive size */}
        <motion.img
          src="/sage-final.png"
          alt="Sage"
          animate={{
            y: isPlaying ? [0, -5, 0] : [0, -2, 0],
            filter: isPlaying
              ? `drop-shadow(0 0 25px ${currentMahadasha.color || '#edc161'})`
              : 'drop-shadow(0 0 12px rgba(237, 193, 97, 0.4))'
          }}
          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
          style={{ 
            width: isWebView ? 'clamp(60px, 8vw, 100px)' : 'clamp(50px, 12vw, 80px)', 
            height: 'auto', 
            margin: '0 auto' 
          }}
        />

        {/* Play/Pause Button */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}
        >
          {storyComplete ? (
            // Complete state - Replay + Download (landing) or Replay + Continue (app)
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              style={{ display: 'flex', flexDirection: isLandingPage ? 'column' : 'row', gap: '12px', justifyContent: 'center', alignItems: 'center' }}
            >
              <button
                onClick={handleReplay}
                data-testid="replay-story-btn"
                style={{
                  padding: 'clamp(10px, 2.5vw, 14px) clamp(20px, 5vw, 28px)',
                  fontSize: 'clamp(13px, 3.2vw, 16px)',
                  fontWeight: 600,
                  background: 'transparent',
                  border: '2px solid #edc161',
                  borderRadius: '25px',
                  color: '#edc161',
                  cursor: 'pointer'
                }}
              >
                Replay Story
              </button>
              {isLandingPage ? (
                <button
                  onClick={handleContinue}
                  data-testid="landing-continue-signup-btn"
                  style={{
                    padding: 'clamp(10px, 2.5vw, 14px) clamp(24px, 6vw, 36px)',
                    fontSize: 'clamp(13px, 3.2vw, 16px)',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    background: 'linear-gradient(135deg, #F3E5AB 0%, #D4AF37 50%, #B8860B 100%)',
                    border: '1px solid rgba(255, 235, 170, 0.6)',
                    borderRadius: '28px',
                    color: '#1a0f00',
                    cursor: 'pointer',
                    boxShadow: '0 0 22px rgba(212,175,55,0.5), 0 4px 14px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.4)',
                    textShadow: '0 1px 0 rgba(255,255,255,0.3)',
                  }}
                >
                  Sign Up &middot; Save Story
                </button>
              ) : isReelMode ? (
                <button
                  onClick={() => navigate(-1)}
                  data-testid="back-to-reels-btn"
                  style={{
                    padding: 'clamp(10px, 2.5vw, 14px) clamp(22px, 5.5vw, 32px)',
                    fontSize: 'clamp(13px, 3.2vw, 16px)',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #edc161, #d4a84b)',
                    border: 'none',
                    borderRadius: '25px',
                    color: '#020712',
                    cursor: 'pointer'
                  }}
                >
                  Back to Reels
                </button>
              ) : (
                <button
                  onClick={handleContinue}
                  data-testid="continue-story-btn"
                  style={{
                    padding: 'clamp(10px, 2.5vw, 14px) clamp(24px, 6vw, 36px)',
                    fontSize: 'clamp(13px, 3.2vw, 16px)',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    background: 'linear-gradient(135deg, #F3E5AB 0%, #D4AF37 50%, #B8860B 100%)',
                    border: '1px solid rgba(255, 235, 170, 0.6)',
                    borderRadius: '28px',
                    color: '#1a0f00',
                    cursor: 'pointer',
                    boxShadow: '0 0 22px rgba(212,175,55,0.5), 0 4px 14px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.4)',
                    textShadow: '0 1px 0 rgba(255,255,255,0.3)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 0 28px rgba(212,175,55,0.7), 0 6px 18px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.5)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 0 22px rgba(212,175,55,0.5), 0 4px 14px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.4)'; }}
                >
                  Continue →
                </button>
              )}
            </motion.div>
          ) : (
            // Play/Pause button — Web-First launch shows just the play button (App store badges removed)
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0', justifyContent: 'center' }}>
                <motion.button
                  onClick={handlePlayPauseToggle}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    width: 'clamp(48px, 12vw, 70px)',
                    height: 'clamp(48px, 12vw, 70px)',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #edc161, #d4a84b)',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 0 30px rgba(237, 193, 97, 0.6)',
                    flexShrink: 0
                  }}
                >
                  {isPlaying ? (
                    <Pause style={{ width: 'clamp(18px, 5vw, 26px)', height: 'clamp(18px, 5vw, 26px)', color: '#020712' }} />
                  ) : (
                    <Play style={{ width: 'clamp(18px, 5vw, 26px)', height: 'clamp(18px, 5vw, 26px)', color: '#020712', marginLeft: '3px' }} />
                  )}
                </motion.button>
              </div>
              <p style={{ fontSize: 'clamp(13px, 3.2vw, 16px)', color: '#edc161', fontWeight: 600, margin: 0 }}>
                {isPlaying ? 'Tap to Pause' : playbackPosition[selectedLanguage] > 0 ? 'Tap to Resume' : 
                  (selectedDomain !== 'all' || selectedMahadashaFilter !== null) ? 'Play Filtered Story' : 'Tap to Play'}
              </p>
            </>
          )}
        </motion.div>
      </div>

      {/* ===== TEXT SECTION: Narration Box - takes remaining space ===== */}
      <div style={{ 
        flex: isWebView ? '1 1 auto' : 2, 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'flex-start',
        minHeight: isWebView ? '180px' : 0,
        padding: '0 clamp(16px, 5vw, 32px)',
        overflow: 'hidden',
        maxWidth: isWebView ? 'clamp(600px, 70vw, 1100px)' : 'none',
        margin: isWebView ? '0 auto' : 0,
        width: '100%'
      }}>
        {/* Text box - NO AnimatePresence to prevent blinking */}
        {displayedText && (
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
            {/* Speech bubble pointer */}
            <div style={{
              width: 0,
              height: 0,
              margin: '0 auto',
              borderLeft: '10px solid transparent',
              borderRight: '10px solid transparent',
              borderBottom: `10px solid ${currentMahadasha.color ? currentMahadasha.color + '40' : 'rgba(237, 193, 97, 0.3)'}`
            }} />
            
            {/* Text box - content-sized, scrollable when overflow */}
            <div 
              id="past-text-box"
              style={{
              padding: 'clamp(14px, 3.5vw, 20px) clamp(16px, 4vw, 24px)',
              background: `linear-gradient(135deg, ${currentMahadasha.color ? currentMahadasha.color + '18' : 'rgba(237, 193, 97, 0.12)'}, rgba(2, 7, 18, 0.95))`,
              backdropFilter: 'blur(20px)',
              borderRadius: 'clamp(12px, 3vw, 16px)',
              border: `1.5px solid ${currentMahadasha.color ? currentMahadasha.color + '50' : 'rgba(237, 193, 97, 0.35)'}`,
              maxHeight: '100%',
              overflowY: 'auto',
              touchAction: 'pan-y',
              transition: 'border-color 0.5s ease, background 0.5s ease',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}>
              <p style={{
                fontSize: 'clamp(14px, 3.8vw, 18px)',
                lineHeight: '1.8',
                color: '#F0F0F0',
                margin: 0,
                textAlign: 'center',
                whiteSpace: 'pre-line',
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
                padding: '0 4px'
              }}>
                {storyComplete ? (() => {
                    if (selectedDomain === 'all' && selectedMahadashaFilter === null) {
                      return fullStoryTextRef.current[selectedLanguage] || displayedText;
                    }
                    const filtered = getFilteredChunks(selectedLanguage);
                    return filtered.map(c => c.text).join('\n\n') || displayedText;
                  })() : displayedText}
              </p>
            </div>

            {/* Current Mahadasha Indicator - Compact */}
            {currentMahadashaIndex >= 0 && currentMahadasha.lord && (
              <div style={{ 
                textAlign: 'center', 
                marginTop: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.3s ease'
              }}>
                <span style={{ 
                  fontSize: 'clamp(22px, 5.5vw, 32px)', 
                  textShadow: `0 0 15px ${currentMahadasha.color || '#edc161'}` 
                }}>
                  {currentMahadasha.icon || PLANET_ICONS[currentMahadasha.lord] || '☉'}
                </span>
                <p style={{
                  fontSize: 'clamp(12px, 3vw, 15px)',
                  fontWeight: 600,
                  color: currentMahadasha.color || '#edc161',
                  margin: 0
                }}>
                  {currentMahadasha.lord} Mahadasha • {currentMahadasha.years}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ===== BOTTOM SECTION: Timeline (~20%) ===== */}
      <div style={{ 
        flexShrink: 0,
        // Extra bottom padding so two-line labels (planet name + year range) never
        // clip on iPhones — works in tandem with the safe-area padding on the root.
        padding: '8px 14px 14px',
        maxWidth: isWebView ? 'clamp(600px, 70vw, 1100px)' : 'none',
        margin: isWebView ? '0 auto' : 0,
        width: '100%'
      }}>
        {showFilterHints && (
          <div style={{
            textAlign: 'center', paddingBottom: '4px',
            fontSize: 'clamp(10px, 2.5vw, 13px)', color: 'rgba(237,193,97,0.6)', fontStyle: 'italic'
          }}>
            Tap a Mahadasha to hear its story
          </div>
        )}
        <div style={{ position: 'relative', minHeight: 'clamp(82px, 14vh, 100px)', width: '100%' }}>
          {/* Base line - positioned at 25% from top so labels fit below */}
          <div style={{
            position: 'absolute',
            top: '22px',
            left: '5%',
            right: '5%',
            height: '2px',
            background: 'rgba(237, 193, 97, 0.25)'
          }} />

          {/* Progress line */}
          <motion.div
            animate={{
              scaleX: currentMahadashaIndex >= 0 ? (currentMahadashaIndex + 1) / pastMahadashas.length : 0,
              opacity: isPlaying || displayedText ? 1 : 0
            }}
            transition={{ duration: 0.8 }}
            style={{
              position: 'absolute',
              top: '22px',
              left: '5%',
              right: '5%',
              height: '3px',
              background: '#edc161',
              transformOrigin: 'left',
              borderRadius: '2px',
              boxShadow: '0 0 12px #edc161'
            }}
          />

          {/* Timeline dots - Responsive */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '0 5%',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '100%',
            alignItems: 'flex-start'
          }}>
            {pastMahadashas.map((period, idx) => {
              const isActive = currentMahadashaIndex === idx;
              const isPast = currentMahadashaIndex > idx;
              const isFilterSelected = selectedMahadashaFilter === idx;
              const planetColor = period.color || PLANET_COLORS[period.lord] || '#edc161';
              // Mahadasha filter clicks allowed ONLY on "View All Domains" screen (no mode=reel).
              // In individual domain reel mode, timeline is display-only.
              const filterEnabled = !isReelMode;

              return (
                <div
                  key={idx}
                  onClick={filterEnabled ? () => handleMahadashaFilterClick(idx) : undefined}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    cursor: filterEnabled ? 'pointer' : 'default',
                    position: 'relative',
                    zIndex: isActive || isFilterSelected ? 50 : 10,
                    minWidth: isActive ? '50px' : '35px',
                    paddingTop: '10px',
                  }}
                  data-testid={`mahadasha-dot-${idx}`}
                >
                  {/* Glow animations - show for active mahadasha */}
                  {(isActive || isFilterSelected) && (
                    <>
                      <motion.div
                        animate={{ scale: [1, 1.6, 1], opacity: [0.4, 0, 0.4] }}
                        transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                        style={{
                          position: 'absolute',
                          top: '8px',
                          width: 'clamp(40px, 10vw, 50px)',
                          height: 'clamp(40px, 10vw, 50px)',
                          borderRadius: '50%',
                          border: `2px solid ${planetColor}`,
                          pointerEvents: 'none'
                        }}
                      />
                      <motion.div
                        animate={{ scale: [1, 1.3, 1], opacity: [0.6, 0, 0.6] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                        style={{
                          position: 'absolute',
                          top: '8px',
                          width: 'clamp(32px, 8vw, 42px)',
                          height: 'clamp(32px, 8vw, 42px)',
                          borderRadius: '50%',
                          border: `2px solid ${planetColor}`,
                          pointerEvents: 'none'
                        }}
                      />
                    </>
                  )}
                  
                  {/* Main dot with icon - sits ON the line */}
                  <motion.div
                    animate={{
                      width: (isActive || isFilterSelected) ? 'clamp(28px, 8vw, 36px)' : isPast ? 'clamp(12px, 3vw, 14px)' : 'clamp(10px, 2.5vw, 12px)',
                      height: (isActive || isFilterSelected) ? 'clamp(28px, 8vw, 36px)' : isPast ? 'clamp(12px, 3vw, 14px)' : 'clamp(10px, 2.5vw, 12px)',
                      borderColor: (isActive || isFilterSelected) ? planetColor : isPast ? '#edc161' : 'rgba(237, 193, 97, 0.4)'
                    }}
                    transition={{ duration: 0.5 }}
                    style={{
                      borderRadius: '50%',
                      background: (isActive || isFilterSelected)
                        ? `radial-gradient(circle, ${planetColor}60, ${planetColor}20)`
                        : isPast 
                          ? '#edc161'
                          : 'rgba(237, 193, 97, 0.2)',
                      border: `2px solid ${(isActive || isFilterSelected) ? planetColor : isPast ? '#edc161' : 'rgba(237, 193, 97, 0.4)'}`,
                      boxShadow: (isActive || isFilterSelected)
                        ? `0 0 15px ${planetColor}, 0 0 30px ${planetColor}50`
                        : isPast
                          ? '0 0 4px rgba(237, 193, 97, 0.3)'
                          : 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: (isActive || isFilterSelected) ? 'clamp(12px, 4vw, 16px)' : '0px'
                    }}
                  >
                    {(isActive || isFilterSelected) && (
                      <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        {period.icon || PLANET_ICONS[period.lord]}
                      </motion.span>
                    )}
                  </motion.div>
                  
                  {/* Labels BELOW the dot - Year range and Planet name */}
                  <div style={{ textAlign: 'center', marginTop: '4px', lineHeight: 1.2 }}>
                    {/* Planet name */}
                    <span style={{
                      fontSize: (isActive || isFilterSelected) ? 'clamp(11px, 2.8vw, 14px)' : 'clamp(9px, 2.3vw, 12px)',
                      color: (isActive || isFilterSelected) ? planetColor : isPast ? '#edc161' : 'rgba(237, 193, 97, 0.6)',
                      fontWeight: 700,
                      display: 'block'
                    }}>
                      {period.lord}
                    </span>
                    {/* Year range */}
                    <span style={{
                      fontSize: (isActive || isFilterSelected) ? 'clamp(10px, 2.5vw, 13px)' : 'clamp(8px, 2vw, 11px)',
                      color: (isActive || isFilterSelected) ? planetColor : isPast ? 'rgba(237, 193, 97, 0.8)' : 'rgba(237, 193, 97, 0.5)',
                      fontWeight: 600,
                      display: 'block',
                      whiteSpace: 'nowrap'
                    }}>
                      {period.display_years || period.years}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

    </div>

    {/* Download CTA Overlay for Landing Page Visitors */}
    {showDownloadCTA && (
      <motion.div
        data-testid="download-cta-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(2,7,18,0.92)',
          backdropFilter: 'blur(12px)',
          display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
          padding: '40px 24px', textAlign: 'center'
        }}
      >
        {/* Sage avatar */}
        <img src="/sage-final.png" alt="Sage" style={{ width: 'clamp(80px, 20vw, 120px)', height: 'auto', marginBottom: '24px', opacity: 0.9 }} />
        
        <p style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 'clamp(22px, 5.5vw, 30px)', color: 'rgba(255,255,255,0.9)', fontWeight: 400, margin: '0 0 8px 0' }}>
          Your Journey Has Just Begun
        </p>
        <p style={{ fontSize: 'clamp(13px, 3.2vw, 15px)', color: 'rgba(255,255,255,0.45)', maxWidth: '360px', lineHeight: 1.7, margin: '0 0 32px 0', fontWeight: 300 }}>
          Get Future Predictions, Audio Narration, and 8 Life Domain Deep-Dives on the app.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', maxWidth: '320px' }}>
          <button data-testid="signup-phone-cta" onClick={() => {
            localStorage.setItem('past_story_viewed', 'true');
            localStorage.setItem('free_story_used', 'true');
            pushStoryStateToServer({ past_story_viewed: true, free_story_used: true, landing_flow: false });
            navigate('/login');
          }} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
            padding: '16px 24px', borderRadius: '14px',
            background: 'linear-gradient(135deg, #edc161, #d4a84b)',
            color: '#020712', textDecoration: 'none', fontSize: '15px', fontWeight: 700,
            border: 'none', cursor: 'pointer', letterSpacing: '0.04em'
          }}>
            Sign Up · Save Your Story
          </button>
          <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', textAlign: 'center', margin: '4px 0 0 0', letterSpacing: '0.08em' }}>
            Phone OTP &middot; 10 seconds &middot; No password
          </p>
        </div>

        <button
          data-testid="replay-from-cta"
          onClick={() => setShowDownloadCTA(false)}
          style={{ marginTop: '24px', padding: '10px 20px', background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline' }}
        >
          Replay Story
        </button>
      </motion.div>
    )}

    </div>
  );
};

export default PastStoryRevolutionary;
